import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import SchedulerIntelligenceView from '../components/SchedulerIntelligenceView';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { API_BASE_URL } from '../config';
import {
  Calendar, Check, X, Pause, RefreshCw, AlertTriangle,
  Send, CalendarDays, History, Play, CheckCircle2, XCircle, Clock, Info, Trash2,
  GripVertical, ArrowLeftRight, Search, Move, ShieldCheck, ShieldAlert, Wrench, Sparkles, Save, Users, Plus,
  CheckSquare, Square, ChevronLeft, ChevronRight
} from 'lucide-react';

interface PendingItem {
  afimaCampaignId: string;
  scheduledId?: string;
  id?: string;
  name?: string;
  systemId: string;
  campaignName: string;
  subjectLine: string;
  previewText: string;
  scheduledDate: string;
  status: 'pending' | 'approved' | 'dispatched' | 'scheduled' | 'rejected' | 'held' | 'sent' | 'failed';
  source: 'auto-scheduler' | 'manual';
  weekId: string;
  slotTime: string;
  slotIndex: number;
  includeNL: boolean;
  textEdition: string;
  score: number;
  scoreBreakdown: {
    openRate: number;
    revenue: number;
    clickQuality: number;
    recency: number;
    unsub: number;
    crossEsp: number;
  };
  reasoning: string;
  sendId: string;
  dryRun?: boolean;
  dispatchedAt?: string;
  approvedAt?: string;
  sentAt?: string;
  espCampaignId?: string;
  guardrailIssues?: string[];
  holdReason?: string;
  rejectReason?: string;
  htmlContent?: string;
  openRate?: number;
  delivered?: number;
  unsubs?: number;
  validation?: {
    isValid: boolean;
    healthScore: number;
    issues: string[];
    warnings: string[];
  };
  dispatchPayload?: {
    listId?: string;
    senderId?: string;
    genderSegment?: string[];
    includedSegments?: string[];
    excludedSegments?: string[];
    htmlContent?: string;
    newsletterId?: string;
  };
}

interface WeekPlan {
  espId: string;
  weekId: string;
  items: PendingItem[];
  byDate: Record<string, PendingItem[]>;
  summary: {
    total: number;
    pending: number;
    approved: number;
    dispatched: number;
    sent?: number;
    rejected: number;
    held: number;
  };
}

export interface ProgressModalState {
  open: boolean;
  title: string;
  subtitle?: string;
  stage: 'preparing' | 'validating' | 'processing' | 'dispatching' | 'completed' | 'error';
  progress: number;
  currentMessage?: string;
  completedCount?: number;
  totalCount?: number;
  errorMsg?: string;
  resultsSummary?: { success: number; failed: number; message?: string };
}

function formatCopenhagenTime(slotTime?: string, scheduledDate?: string): string {
  // If scheduledDate has UTC timezone indicator (Z or offset) or ISO timestamp, convert to Europe/Copenhagen
  const dateToConvert = scheduledDate || (slotTime && slotTime.includes('T') ? slotTime : undefined);
  if (dateToConvert && (dateToConvert.includes('T') || dateToConvert.includes('Z') || dateToConvert.includes('+'))) {
    try {
      const dt = new Date(dateToConvert);
      if (!isNaN(dt.getTime())) {
        return dt.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Europe/Copenhagen'
        });
      }
    } catch (e) {}
  }

  if (slotTime && slotTime.includes(':')) {
    const clean = slotTime.trim();
    return clean.length === 5 ? clean : clean.slice(0, 5);
  }

  return '10:00';
}

interface EspTheme {
  name: string;
  short: string;
  cardBg: string;
  cardBorder: string;
  cardHover: string;
  tagBg: string;
  tagText: string;
  tagBorder: string;
  dotColor: string;
}

function getEspTheme(systemId?: string, espIdFallback?: string): EspTheme {
  const s = (systemId || espIdFallback || '').toLowerCase();
  
  if (s.includes('feedblitz') || s.startsWith('fb_')) {
    return {
      name: 'FeedBlitz',
      short: 'FB',
      cardBg: 'bg-sky-50/70',
      cardBorder: 'border-sky-200',
      cardHover: 'hover:border-sky-400 hover:bg-sky-100/70',
      tagBg: 'bg-sky-100',
      tagText: 'text-sky-800',
      tagBorder: 'border-sky-200',
      dotColor: 'bg-sky-500',
    };
  }
  if (s.includes('simplyloan') || s.startsWith('br_sl_') || s.startsWith('sl_')) {
    return {
      name: 'SimplyLoan',
      short: 'SL',
      cardBg: 'bg-amber-50/70',
      cardBorder: 'border-amber-200',
      cardHover: 'hover:border-amber-400 hover:bg-amber-100/70',
      tagBg: 'bg-amber-100',
      tagText: 'text-amber-800',
      tagBorder: 'border-amber-200',
      dotColor: 'bg-amber-500',
    };
  }
  if (s.includes('gr_se2') || s.includes('getresponse_se2')) {
    return {
      name: 'GR SE2',
      short: 'GR2',
      cardBg: 'bg-indigo-50/70',
      cardBorder: 'border-indigo-200',
      cardHover: 'hover:border-indigo-400 hover:bg-indigo-100/70',
      tagBg: 'bg-indigo-100',
      tagText: 'text-indigo-800',
      tagBorder: 'border-indigo-200',
      dotColor: 'bg-indigo-500',
    };
  }
  if (s.includes('gr_') || s.includes('getresponse')) {
    return {
      name: 'GetResponse',
      short: 'GR',
      cardBg: 'bg-purple-50/70',
      cardBorder: 'border-purple-200',
      cardHover: 'hover:border-purple-400 hover:bg-purple-100/70',
      tagBg: 'bg-purple-100',
      tagText: 'text-purple-800',
      tagBorder: 'border-purple-200',
      dotColor: 'bg-purple-500',
    };
  }
  if (s.includes('ms_') || s.includes('moosend')) {
    return {
      name: 'Moosend',
      short: 'MS',
      cardBg: 'bg-rose-50/70',
      cardBorder: 'border-rose-200',
      cardHover: 'hover:border-rose-400 hover:bg-rose-100/70',
      tagBg: 'bg-rose-100',
      tagText: 'text-rose-800',
      tagBorder: 'border-rose-200',
      dotColor: 'bg-rose-500',
    };
  }
  if (s.includes('dp_') || s.includes('drip')) {
    return {
      name: 'Drip',
      short: 'DP',
      cardBg: 'bg-pink-50/70',
      cardBorder: 'border-pink-200',
      cardHover: 'hover:border-pink-400 hover:bg-pink-100/70',
      tagBg: 'bg-pink-100',
      tagText: 'text-pink-800',
      tagBorder: 'border-pink-200',
      dotColor: 'bg-pink-500',
    };
  }
  if (s.includes('ml_') || s.includes('mailerlite')) {
    return {
      name: 'MailerLite',
      short: 'ML',
      cardBg: 'bg-cyan-50/70',
      cardBorder: 'border-cyan-200',
      cardHover: 'hover:border-cyan-400 hover:bg-cyan-100/70',
      tagBg: 'bg-cyan-100',
      tagText: 'text-cyan-800',
      tagBorder: 'border-cyan-200',
      dotColor: 'bg-cyan-500',
    };
  }
  if (s.includes('br_') || s.includes('brevo')) {
    return {
      name: 'Brevo',
      short: 'BR',
      cardBg: 'bg-emerald-50/70',
      cardBorder: 'border-emerald-200',
      cardHover: 'hover:border-emerald-400 hover:bg-emerald-100/70',
      tagBg: 'bg-emerald-100',
      tagText: 'text-emerald-800',
      tagBorder: 'border-emerald-200',
      dotColor: 'bg-emerald-500',
    };
  }
  return {
    name: systemId || 'ESP',
    short: 'ESP',
    cardBg: 'bg-slate-50/70',
    cardBorder: 'border-slate-200',
    cardHover: 'hover:border-slate-400 hover:bg-slate-100/70',
    tagBg: 'bg-slate-100',
    tagText: 'text-slate-700',
    tagBorder: 'border-slate-200',
    dotColor: 'bg-slate-500',
  };
}

function formatEspBadge(systemId?: string, espIdFallback?: string): string {
  return getEspTheme(systemId, espIdFallback).name;
}

function OperationProgressModal({
  state,
  onClose,
}: {
  state: ProgressModalState;
  onClose: () => void;
}) {
  if (!state.open) return null;

  const isComplete = state.stage === 'completed';
  const isError = state.stage === 'error';
  const isProcessing = !isComplete && !isError;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100 flex flex-col relative animate-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Header accent strip */}
        <div className={`h-2 w-full ${isError ? 'bg-red-500' : isComplete ? 'bg-emerald-500' : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-500 animate-pulse'}`} />

        <div className="p-6 text-center space-y-5">
          {/* Icon state */}
          <div className="flex justify-center">
            {isProcessing && (
              <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center shadow-inner relative border border-indigo-100">
                <Send className="w-8 h-8 text-indigo-600 animate-bounce" />
                <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-indigo-600"></span>
                </span>
              </div>
            )}
            {isComplete && (
              <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center shadow-inner animate-in zoom-in-75 duration-300">
                <CheckCircle2 className="w-10 h-10 text-emerald-500" />
              </div>
            )}
            {isError && (
              <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center shadow-inner">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
            )}
          </div>

          {/* Title & Subtitle */}
          <div>
            <h3 className="text-lg font-bold text-gray-900">{state.title}</h3>
            {state.subtitle && (
              <p className="text-xs text-gray-500 mt-1 font-medium">{state.subtitle}</p>
            )}
          </div>

          {/* Progress Bar & Status Text */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-semibold text-gray-600 px-1">
              <span className="truncate max-w-[280px] text-left">{state.currentMessage || (isComplete ? 'All tasks finished successfully' : 'Processing...')}</span>
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold shrink-0 ${isComplete ? 'bg-emerald-100 text-emerald-800' : 'bg-indigo-100 text-indigo-800'}`}>
                {Math.round(state.progress)}%
              </span>
            </div>

            <div className="w-full bg-gray-100 rounded-full h-3.5 overflow-hidden p-0.5 border border-gray-200 shadow-inner">
              <div
                className={`h-full rounded-full transition-all duration-300 ease-out ${
                  isError 
                    ? 'bg-red-500' 
                    : isComplete 
                      ? 'bg-emerald-500' 
                      : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600 bg-[length:200%_100%] animate-pulse'
                }`}
                style={{ width: `${Math.min(100, Math.max(6, state.progress))}%` }}
              />
            </div>

            {state.totalCount !== undefined && state.totalCount > 0 ? (
              <div className="flex justify-between items-center text-[11px] text-gray-400 px-1 pt-1 font-medium">
                <span>Progress:</span>
                <span className="font-bold text-gray-700">{state.completedCount || 0} of {state.totalCount} campaigns</span>
              </div>
            ) : null}
          </div>

          {/* Error Message if any */}
          {isError && state.errorMsg && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-left text-xs text-red-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
              <div>
                <strong>Error:</strong> {state.errorMsg}
              </div>
            </div>
          )}

          {/* Summary Details on Complete */}
          {isComplete && state.resultsSummary && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 text-left space-y-1">
              <div className="font-semibold flex items-center gap-1.5">
                <Check className="w-4 h-4 text-emerald-600" />
                <span>
                  {state.resultsSummary.message || `${state.resultsSummary.success} campaign draft(s) created (Pending Approval)`}
                </span>
              </div>
              {state.resultsSummary.failed > 0 && (
                <div className="text-yellow-700 text-[11px]">
                  ⚠️ {state.resultsSummary.failed} item(s) skipped or failed
                </div>
              )}
            </div>
          )}

          {/* Action button */}
          {(isComplete || isError) && (
            <div className="pt-2">
              <button
                type="button"
                onClick={onClose}
                className={`w-full py-2.5 px-4 rounded-xl text-sm font-bold text-white transition-all shadow-md cursor-pointer ${
                  isError 
                    ? 'bg-gray-800 hover:bg-gray-900' 
                    : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20'
                }`}
              >
                {isError ? 'Close' : 'Done & View Plan'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Format date as local YYYY-MM-DD without UTC timezone skew
function formatYYYYMMDD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDateLabel(dateStr: string) {
  if (!dateStr) return { day: '', label: '' };
  const d = new Date(dateStr + 'T12:00:00');
  if (isNaN(d.getTime())) return { day: '', label: dateStr };
  const day = d.toLocaleDateString('en-US', { weekday: 'short' });
  const month = d.toLocaleDateString('en-US', { month: 'short' });
  const num = d.getDate();
  return { day, label: `${month} ${num}` };
}

function addDays(dateStr: string, numDays: number): string {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + numDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function calcDaysBetween(startStr: string, endStr: string): number {
  if (!startStr || !endStr) return 1;
  const s = new Date(startStr + 'T12:00:00').getTime();
  const e = new Date(endStr + 'T12:00:00').getTime();
  const diff = Math.round((e - s) / 86400000);
  return Math.max(1, diff + 1);
}

export function filterTextsForEsp(texts: any[], espId: string): any[] {
  if (!texts || texts.length === 0) return [];
  const esp = (espId || '').toLowerCase().trim();
  
  const getVer = (t: any) => String(t.textId || t.versionId || t.id || t.name || '').toLowerCase().trim();

  // 1. FeedBlitz (feedblitz_se, fb_se, feedblitz_no, fb_no, feedblitz_fi, fb_fi, feedblitz_de, fb_de):
  // Strictly matches 'a' / 'A' variants (e.g. TA1a, TA2a, TA3a, TA4a, TA5a) or plain TA variants
  if (esp.startsWith('feedblitz') || esp.startsWith('fb_') || esp === 'fb') {
    const aEds = texts.filter(t => getVer(t).endsWith('a'));
    if (aEds.length > 0) return aEds;
    const plainTa = texts.filter(t => getVer(t).startsWith('ta') && !['b', 'c', 'd', 'e'].some(s => getVer(t).endsWith(s)));
    if (plainTa.length > 0) return plainTa;
    return texts.filter(t => !['b', 'c', 'd', 'e'].some(s => getVer(t).endsWith(s)));
  }

  // 2. GetResponse Svenska Lån (gr_se2):
  // Strictly matches 'e' / 'E' variants (e.g. TA1e, TA2e, TA3e, TA4e, TA5e)
  if (esp === 'gr_se2' || esp.includes('se2')) {
    const eEds = texts.filter(t => getVer(t).endsWith('e'));
    if (eEds.length > 0) return eEds;
    return texts.filter(t => !['a', 'b', 'c', 'd'].some(s => getVer(t).endsWith(s)));
  }

  // 3. GetResponse standard (gr_se, gr_no, gr_fi, gr_de, getresponse_*):
  // Matches 'b' / 'B' variants (e.g. TA1b, TA2b, TA3b, TA4b, TA5b, TG1b...) or plain TG variants
  if (esp.startsWith('gr_') || esp.includes('getresponse') || esp === 'gr') {
    const bEds = texts.filter(t => getVer(t).endsWith('b'));
    if (bEds.length > 0) return bEds;
    const plainTg = texts.filter(t => getVer(t).startsWith('tg') && !['a', 'c', 'd', 'e'].some(s => getVer(t).endsWith(s)));
    if (plainTg.length > 0) return plainTg;
    return texts.filter(t => !['a', 'c', 'd', 'e'].some(s => getVer(t).endsWith(s)));
  }

  // 4. Moosend (ms_se, ms_*, moosend_*):
  // Matches 'c' / 'C' variants (e.g. TA1c, TA2c, TA3c, TA4c, TA5c, TG1c...)
  if (esp.startsWith('ms_') || esp.includes('moosend') || esp === 'ms') {
    const cEds = texts.filter(t => getVer(t).endsWith('c'));
    if (cEds.length > 0) return cEds;
    return texts.filter(t => !['a', 'b', 'd', 'e'].some(s => getVer(t).endsWith(s)));
  }

  // 5. SimplyLoan (br_sl_se, br_sl_no, br_sl_fi, sl_*, simplyloan_*):
  // Matches 'd' / 'D' variants (e.g. TA1d, TA2d, TA3d, TA4d, TA5d) or TB editions
  if (esp.includes('sl') || esp.includes('simplyloan') || esp.startsWith('br_sl_')) {
    const dEds = texts.filter(t => getVer(t).endsWith('d'));
    if (dEds.length > 0) return dEds;
    const tbEds = texts.filter(t => getVer(t).startsWith('tb'));
    if (tbEds.length > 0) return tbEds;
    return texts.filter(t => !['a', 'b', 'c', 'e', 'f'].some(s => getVer(t).endsWith(s)));
  }

  // 6. MailerLite (ml_fi, ml_*, mailerlite_*):
  // Matches 'e' / 'E' variants or 'c' variants
  if (esp.startsWith('ml_') || esp.includes('mailerlite') || esp === 'ml') {
    const eEds = texts.filter(t => getVer(t).endsWith('e'));
    if (eEds.length > 0) return eEds;
    const cEds = texts.filter(t => getVer(t).endsWith('c'));
    if (cEds.length > 0) return cEds;
    return texts.filter(t => !['a', 'b', 'd', 'f'].some(s => getVer(t).endsWith(s)));
  }

  // 7. Brevo standard (br_se, br_no, br_fi, brevo_*):
  // Matches 'd' / 'D' variants (e.g. TA1d, TA2d, TA3d, TA4d, TA5d)
  if (esp.startsWith('br_') || esp.includes('brevo') || esp === 'br') {
    const dEds = texts.filter(t => getVer(t).endsWith('d'));
    if (dEds.length > 0) return dEds;
    return texts.filter(t => !['a', 'b', 'c', 'e', 'f'].some(s => getVer(t).endsWith(s)));
  }

  // 8. Drip (dp_se, drip_se, dp_*):
  // Matches dedicated 'f' / 'F' variants (e.g. TA1f..TA5f) or plain TA variants
  if (esp.startsWith('dp_') || esp.includes('drip') || esp === 'dp') {
    const fEds = texts.filter(t => getVer(t).endsWith('f'));
    if (fEds.length > 0) return fEds;
    const plainTa = texts.filter(t => getVer(t).startsWith('ta') && !['a', 'b', 'c', 'd', 'e', 'f'].some(s => getVer(t).endsWith(s)));
    if (plainTa.length > 0) return plainTa;
    return texts.filter(t => !['a', 'b', 'c', 'd', 'e', 'f'].some(s => getVer(t).endsWith(s)));
  }

  return texts;
}

export function getAvailableEsps(currentEspId: string): { id: string; label: string; shortName: string; letter: string; badgeBg: string }[] {
  const isCountry = currentEspId.startsWith('all_');
  const geo = isCountry ? currentEspId.replace('all_', '') : (currentEspId.split('_').pop() || 'se');
  
  if (geo === 'se' || currentEspId === 'all_se') {
    return [
      { id: 'feedblitz_se', label: 'FeedBlitz Sweden', shortName: 'FeedBlitz', letter: 'A', badgeBg: 'bg-sky-500' },
      { id: 'gr_se', label: 'GetResponse (Sparkredit)', shortName: 'GetResponse', letter: 'B', badgeBg: 'bg-purple-500' },
      { id: 'gr_se2', label: 'GetResponse (Svenska Lån)', shortName: 'GR SE2', letter: 'E', badgeBg: 'bg-indigo-500' },
      { id: 'ms_se', label: 'Moosend (Swiftkredit)', shortName: 'Moosend', letter: 'C', badgeBg: 'bg-rose-500' },
      { id: 'dp_se', label: 'Drip (Enkelt Lån)', shortName: 'Drip', letter: 'F', badgeBg: 'bg-pink-500' },
    ];
  }
  if (geo === 'no' || currentEspId === 'all_no') {
    return [
      { id: 'feedblitz_no', label: 'FeedBlitz Norway', shortName: 'FeedBlitz', letter: 'A', badgeBg: 'bg-sky-500' },
      { id: 'gr_no', label: 'GetResponse Norway', shortName: 'GetResponse', letter: 'B', badgeBg: 'bg-purple-500' },
    ];
  }
  if (geo === 'fi' || currentEspId === 'all_fi') {
    return [
      { id: 'feedblitz_fi', label: 'FeedBlitz Finland', shortName: 'FeedBlitz', letter: 'A', badgeBg: 'bg-sky-500' },
      { id: 'gr_fi', label: 'GetResponse Finland', shortName: 'GetResponse', letter: 'B', badgeBg: 'bg-purple-500' },
      { id: 'ml_fi', label: 'MailerLite Finland', shortName: 'MailerLite', letter: 'C', badgeBg: 'bg-cyan-500' },
      { id: 'br_fi', label: 'Brevo Finland', shortName: 'Brevo', letter: 'D', badgeBg: 'bg-emerald-500' },
    ];
  }
  if (geo === 'de' || currentEspId === 'all_de') {
    return [
      { id: 'feedblitz_de', label: 'FeedBlitz Germany', shortName: 'FeedBlitz', letter: 'A', badgeBg: 'bg-sky-500' },
    ];
  }
  if (geo === 'sl' || currentEspId === 'all_sl' || currentEspId.startsWith('br_sl_')) {
    return [
      { id: 'br_sl_se', label: 'SimplyLoan Sweden', shortName: 'SimplyLoan SE', letter: 'D', badgeBg: 'bg-amber-500' },
      { id: 'br_sl_no', label: 'SimplyLoan Norway', shortName: 'SimplyLoan NO', letter: 'D', badgeBg: 'bg-amber-500' },
      { id: 'br_sl_fi', label: 'SimplyLoan Finland', shortName: 'SimplyLoan FI', letter: 'D', badgeBg: 'bg-amber-500' },
    ];
  }
  return [
    { id: currentEspId, label: ESP_LABELS[currentEspId] || currentEspId, shortName: ESP_LABELS[currentEspId] || currentEspId, letter: 'A', badgeBg: 'bg-indigo-500' }
  ];
}

const ESP_LABELS: Record<string, string> = {
  all_se: '🇸🇪 All Sweden (FeedBlitz, GetResponse, Moosend, Drip)',
  all_no: '🇳🇴 All Norway (FeedBlitz, GetResponse)',
  all_fi: '🇫🇮 All Finland (FeedBlitz, GR, MailerLite, Brevo)',
  all_de: '🇩🇪 All Germany (FeedBlitz DE)',
  all_sl: '⚡ All SimplyLoan (SE, NO, FI)',
  feedblitz_se: 'FeedBlitz Sweden',
  feedblitz_no: 'FeedBlitz Norway',
  feedblitz_fi: 'FeedBlitz Finland',
  feedblitz_de: 'FeedBlitz Germany',
  gr_se: 'GetResponse Sweden (Sparkredit)',
  gr_se2: 'GetResponse Sweden (Svenska Lån)',
  gr_no: 'GetResponse Norway (Norske Tilbud)',
  gr_fi: 'GetResponse Finland (Suomalaisetlainat)',
  ms_se: 'Moosend Sweden (Swiftkredit)',
  dp_se: 'Drip Sweden (Enkelt Lån)',
  ml_fi: 'MailerLite Finland (Tarjouksesi)',
  br_fi: 'Brevo Finland (Lainatsuomessa)',
  br_sl_se: 'SimplyLoan Brevo (Sweden)',
  br_sl_no: 'SimplyLoan Brevo (Norway)',
  br_sl_fi: 'SimplyLoan Brevo (Finland)',
};

export default function AutoScheduleDashboard({ initialTab }: { initialTab?: 'planner' | 'intelligence' } = {}) {
  const location = useLocation();
  const [mainTab, setMainTab] = useState<'planner' | 'intelligence'>(() =>
    initialTab === 'intelligence' ||
    location.pathname.includes('scheduler-intelligence') ||
    location.search.includes('tab=intelligence')
      ? 'intelligence'
      : 'planner'
  );
  useEffect(() => {
    if (
      initialTab === 'intelligence' ||
      location.pathname.includes('scheduler-intelligence') ||
      location.search.includes('tab=intelligence')
    ) {
      setMainTab('intelligence');
    } else if (location.pathname === '/email/auto-schedule' && !location.search.includes('tab=intelligence')) {
      setMainTab('planner');
    }
  }, [initialTab, location.pathname, location.search]);
  const [espId, setEspId] = useState('feedblitz_se');
  const isSlView = espId === 'all_sl' || espId === 'simplyloan' || espId.startsWith('br_sl_') || espId.includes('simplyloan');
  const isSlItem = (item: any) => {
    if (!item) return false;
    const sid = (item.systemId || item.espId || item.esp || '').toLowerCase();
    const name = (item.name || item.campaignName || '').toLowerCase();
    return sid.startsWith('br_sl_') || sid.includes('simplyloan') || sid.startsWith('sl_') || name.startsWith('slse_') || name.startsWith('slno_') || name.startsWith('slfi_');
  };
  const activeFetchIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [weekId, setWeekId] = useState('');
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [weekPlan, setWeekPlan] = useState<WeekPlan | null>(null);
  const [alreadyScheduled, setAlreadyScheduled] = useState<any[]>([]);
  const [todayDispatches, setTodayDispatches] = useState<PendingItem[]>([]);
  const [history, setHistory] = useState<PendingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generateResult, setGenerateResult] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
  const [isManualCampaign, setIsManualCampaign] = useState(false);
  const [userPrompt, setUserPrompt] = useState('');
  const [showPrompt, setShowPrompt] = useState(false);

  // Multi-select & Batch Unschedule State
  const [selectedCampaigns, setSelectedCampaigns] = useState<Map<string, any>>(new Map());

  // Drag and Drop & Swap state
  const [draggedItem, setDraggedItem] = useState<PendingItem | null>(null);
  const [dragOverDate, setDragOverDate] = useState<string | null>(null);
  const [dragOverItemId, setDragOverItemId] = useState<string | null>(null);
  const [swappingItem, setSwappingItem] = useState<PendingItem | null>(null);

  // Quality & Safety Failsafe Audit State
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [auditReport, setAuditReport] = useState<any | null>(null);
  const [isRepairing, setIsRepairing] = useState(false);

  // Operation / Scheduling Progress Modal State
  const [progressModal, setProgressModal] = useState<ProgressModalState>({
    open: false,
    title: '',
    stage: 'preparing',
    progress: 0,
  });

  // Manual Add Campaign State
  const [manualAddOpen, setManualAddOpen] = useState(false);
  const [manualAddDate, setManualAddDate] = useState('');

  // View mode: '7d' | '14d' | 'month' (default '14d')
  const [viewMode, setViewMode] = useState<'7d' | '14d' | 'month'>('14d');
  // Base start date (Tuesday of starting cycle)
  const [baseStartDate, setBaseStartDate] = useState<string>('');

  // Compute Tuesday of current cycle
  const getCurrentTuesday = (): string => {
    const d = new Date();
    const day = d.getDay() === 0 ? 7 : d.getDay(); // 1=Mon .. 7=Sun
    const diffToTue = day === 1 ? 1 : 2 - day;
    const tue = new Date(d);
    tue.setDate(d.getDate() + diffToTue);
    return formatYYYYMMDD(tue);
  };

  // Compute Tuesday of last week's cycle (7 days before current Tuesday)
  const getLastTuesday = (): string => {
    const curTue = new Date(getCurrentTuesday() + 'T12:00:00');
    curTue.setDate(curTue.getDate() - 7);
    return formatYYYYMMDD(curTue);
  };

  // Default base start date: For 14d and month views, default to Last Week's Tuesday so we show Last Week + Current Week
  const getDefaultBaseDate = (mode: '7d' | '14d' | 'month'): string => {
    if (mode === '7d') return getCurrentTuesday();
    return getLastTuesday();
  };

  // Compute ISO week from a date
  const getISOWeek = (d: Date): string => {
    const date = new Date(d.getTime());
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
    const week1 = new Date(date.getFullYear(), 0, 4, 12, 0, 0);
    const weekNum = 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
    return `${date.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
  };

  // The base Tuesday
  const activeBaseDate = useMemo(() => {
    return baseStartDate || getDefaultBaseDate(viewMode);
  }, [baseStartDate, viewMode]);

  // Number of days to display
  const activeDays = useMemo(() => {
    if (viewMode === '7d') return 7;
    if (viewMode === '14d') return 14;
    return 28; // Month view = 4 weeks (28 days)
  }, [viewMode]);

  // Compute display dates for range
  const displayDates = useMemo(() => {
    const start = new Date(activeBaseDate + 'T12:00:00');
    const dates: string[] = [];
    for (let i = 0; i < activeDays; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(formatYYYYMMDD(d));
    }
    return dates;
  }, [activeBaseDate, activeDays]);

  // Split into weekly rows of 7 days each
  const weekRows = useMemo(() => {
    const rows = [];
    const totalWeeks = activeDays / 7;
    const curTue = getCurrentTuesday();
    const lastTue = getLastTuesday();

    for (let w = 0; w < totalWeeks; w++) {
      const weekDates = displayDates.slice(w * 7, (w + 1) * 7);
      const startD = weekDates[0];
      let weekLabel = `Week ${w + 1}`;
      if (startD === lastTue) {
        weekLabel = 'Last Week';
      } else if (startD === curTue) {
        weekLabel = 'Current Week';
      } else {
        const diffWeeks = Math.round((new Date(startD + 'T12:00:00').getTime() - new Date(curTue + 'T12:00:00').getTime()) / (7 * 86400000));
        if (diffWeeks > 0) {
          weekLabel = diffWeeks === 1 ? 'Next Week' : `+${diffWeeks} Weeks`;
        } else if (diffWeeks < 0) {
          weekLabel = `${diffWeeks} Weeks`;
        }
      }

      rows.push({
        weekNum: w + 1,
        title: `${weekLabel} (${formatDateLabel(weekDates[0]).label} – ${formatDateLabel(weekDates[6]).label})`,
        dates: weekDates,
      });
    }
    return rows;
  }, [displayDates, activeDays]);

  // Navigation handlers
  const handlePrev = () => {
    const shift = activeDays;
    const start = new Date(activeBaseDate + 'T12:00:00');
    start.setDate(start.getDate() - shift);
    setBaseStartDate(formatYYYYMMDD(start));
  };

  const handleNext = () => {
    const shift = activeDays;
    const start = new Date(activeBaseDate + 'T12:00:00');
    start.setDate(start.getDate() + shift);
    setBaseStartDate(formatYYYYMMDD(start));
  };

  const handleToday = () => {
    setBaseStartDate(getDefaultBaseDate(viewMode));
  };

  // Dynamic date presets for quick jump
  const datePresets = useMemo(() => {
    const currentTue = getCurrentTuesday();
    const lastTue = getLastTuesday();
    const curDt = new Date(currentTue + 'T12:00:00');
    
    const p2w = new Date(curDt); p2w.setDate(p2w.getDate() - 14);
    const nw = new Date(curDt); nw.setDate(nw.getDate() + 7);
    const i2w = new Date(curDt); i2w.setDate(i2w.getDate() + 14);

    return [
      { label: `Default (Last & Current Week)`, start: lastTue },
      { label: `Current Week (${formatDateLabel(currentTue).label})`, start: currentTue },
      { label: `Next Week (${formatDateLabel(formatYYYYMMDD(nw)).label})`, start: formatYYYYMMDD(nw) },
      { label: `In 2 Weeks (${formatDateLabel(formatYYYYMMDD(i2w)).label})`, start: formatYYYYMMDD(i2w) },
      { label: `Past Range (${formatDateLabel(formatYYYYMMDD(p2w)).label})`, start: formatYYYYMMDD(p2w) },
    ];
  }, [activeDays]);

  // Sync weekId with displayDates[0]
  useEffect(() => {
    if (displayDates.length > 0) {
      setWeekId(getISOWeek(new Date(displayDates[0] + 'T12:00:00')));
    }
  }, [displayDates]);

  // Fetch data for the active date range & ESP with race-condition & stale-response protection
  const fetchWeekPlan = useCallback(async () => {
    if (!espId) return;
    
    // Abort previous in-flight fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const currentFetchId = ++activeFetchIdRef.current;
    const currentEsp = espId;

    setLoading(true);
    setError(null);

    try {
      const startDate = displayDates[0] || activeBaseDate;
      const endDate = displayDates[displayDates.length - 1] || activeBaseDate;

      const [planRes, schedRes, todayRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/auto-schedule/week?espId=${currentEsp}&startDate=${activeBaseDate}&days=${activeDays}`, { signal: controller.signal }),
        fetch(`${API_BASE_URL}/api/auto-scheduler/scheduled?espId=${currentEsp}&startDate=${startDate}&endDate=${endDate}`, { signal: controller.signal }),
        fetch(`${API_BASE_URL}/api/auto-schedule/today?espId=${currentEsp}`, { signal: controller.signal }),
      ]);

      // If another fetch started while this was in-flight, ignore stale response
      if (currentFetchId !== activeFetchIdRef.current) return;

      if (planRes.ok) {
        const data = await planRes.json();
        if (currentFetchId === activeFetchIdRef.current) setWeekPlan(data);
      } else {
        if (currentFetchId === activeFetchIdRef.current) setWeekPlan(null);
      }

      if (schedRes.ok) {
        const sdata = await schedRes.json();
        if (currentFetchId === activeFetchIdRef.current) setAlreadyScheduled(sdata?.campaigns || []);
      } else {
        if (currentFetchId === activeFetchIdRef.current) setAlreadyScheduled([]);
      }

      if (todayRes.ok) {
        const tdata = await todayRes.json();
        if (currentFetchId === activeFetchIdRef.current) setTodayDispatches(tdata?.items || []);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      if (currentFetchId === activeFetchIdRef.current) {
        console.error('Error fetching plan data:', err);
      }
    } finally {
      if (currentFetchId === activeFetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [espId, activeBaseDate, activeDays, displayDates]);

  // Compatibility aliases for existing callers
  const fetchAlreadyScheduled = useCallback(async () => {
    // Included in unified fetchWeekPlan
  }, []);

  const fetchToday = useCallback(async () => {
    // Included in unified fetchWeekPlan
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/history?espId=${espId}&weeks=4`);
      if (res.ok) {
        const data = await res.json();
        setHistory(data?.items || []);
      }
    } catch (e) {
      console.error(e);
    }
  }, [espId]);

  const [syncLoading, setSyncLoading] = useState(false);

  useEffect(() => {
    fetchWeekPlan();
  }, [fetchWeekPlan]);

  useEffect(() => {
    if (historyOpen) fetchHistory();
  }, [historyOpen, fetchHistory]);

  const handleSyncEsp = useCallback(async () => {
    try {
      setSyncLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/sync-esp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ espId })
      });
      const data = await res.json();
      if (data?.success) {
        setGenerateResult(`✅ Synced with ${espId}: ${data.prunedCount || 0} stale/deleted campaigns removed.`);
        await Promise.all([fetchWeekPlan(), fetchAlreadyScheduled(), fetchToday()]);
      } else {
        alert('Sync error: ' + (data?.error || 'Unknown error'));
      }
    } catch (e: any) {
      console.error(e);
      alert('Network error during sync: ' + e.message);
    } finally {
      setSyncLoading(false);
    }
  }, [espId, fetchWeekPlan, fetchAlreadyScheduled, fetchToday]);

  const handleUnscheduleCampaign = useCallback(async (campaign: any) => {
    const name = campaign.campaignName || campaign.name || 'Campaign';
    const scheduledId = campaign.scheduledId || campaign.id || campaign.newsletterId || campaign.dispatchPayload?.newsletterId || campaign.espCampaignId;
    const afimaId = campaign.afimaCampaignId;
    const targetEsp = campaign.systemId || campaign.espId || campaign.esp || espId;
    
    if (!window.confirm(`Are you sure you want to delete and unschedule "${name}" from FeedBlitz / ESP and the calendar?`)) {
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/unschedule-campaign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          espId: targetEsp,
          scheduledId,
          afimaCampaignId: afimaId,
          campaignName: name
        })
      });
      const data = await res.json();
      if (data?.success) {
        setSelectedCampaign(null);
        setGenerateResult(`🗑️ "${name}" unscheduled and removed.`);
        await Promise.all([fetchWeekPlan(), fetchAlreadyScheduled(), fetchToday()]);
      } else {
        alert('Failed to unschedule: ' + (data?.error || 'Unknown error'));
      }
    } catch (e: any) {
      console.error(e);
      alert('Error during unschedule: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [espId, fetchWeekPlan, fetchAlreadyScheduled, fetchToday]);

  const toggleSelectCampaign = useCallback((campaign: any, keyOverride?: string) => {
    const key = keyOverride || campaign.afimaCampaignId || campaign.scheduledId || campaign.id || campaign.newsletterId || (campaign.name ? `manual-${campaign.name}-${campaign.date || campaign.sendOn}` : 'unknown');
    setSelectedCampaigns(prev => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, campaign);
      }
      return next;
    });
  }, []);

  const handleMultiUnschedule = useCallback(async () => {
    const selectedList = Array.from(selectedCampaigns.values());
    if (selectedList.length === 0) return;

    const count = selectedList.length;
    if (!window.confirm(`Are you sure you want to delete and unschedule ${count} selected campaign(s) from FeedBlitz / ESP and the calendar?`)) {
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let failCount = 0;

      // Execute unschedule in parallel for all selected campaigns using the live endpoint
      const promises = selectedList.map(async (c) => {
        const name = c.campaignName || c.name || 'Campaign';
        const scheduledId = c.scheduledId || c.id || c.newsletterId || c.dispatchPayload?.newsletterId || c.espCampaignId;
        const afimaId = c.afimaCampaignId;
        const targetEsp = c.systemId || c.espId || c.esp || espId;

        try {
          const res = await fetch(`${API_BASE_URL}/api/auto-schedule/unschedule-campaign`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              espId: targetEsp,
              scheduledId,
              afimaCampaignId: afimaId,
              campaignName: name
            })
          });
          const data = await res.json();
          if (data?.success || res.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (err) {
          failCount++;
        }
      });

      await Promise.all(promises);

      setSelectedCampaigns(new Map());
      setSelectedCampaign(null);
      if (successCount > 0) {
        setGenerateResult(`🗑️ ${successCount} of ${count} campaign(s) successfully unscheduled and removed.`);
      }
      if (failCount > 0 && successCount === 0) {
        alert('Failed to unschedule selected campaigns.');
      }
      await Promise.all([fetchWeekPlan(), fetchAlreadyScheduled(), fetchToday()]);
    } catch (e: any) {
      console.error(e);
      alert('Error during batch unschedule: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [espId, selectedCampaigns, fetchWeekPlan, fetchAlreadyScheduled, fetchToday]);

  const handleAction = useCallback(async (endpoint: string, payload: any) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(`Action failed: ${endpoint}`);
      await Promise.all([fetchWeekPlan(), fetchAlreadyScheduled(), fetchToday()]);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchWeekPlan, fetchAlreadyScheduled, fetchToday]);

  const handleGenerate = useCallback(async (customParams?: { startDate?: string; days?: number; prompt?: string }) => {
    setGenerating(true);
    setGenerateResult(null);

    const isCountrySync = espId.startsWith('all_');
    const targetGeo = isCountrySync ? espId.replace('all_', '') : (espId.split('_').pop() || 'se');
    const displayTarget = isCountrySync ? `ALL ${targetGeo.toUpperCase()} ESPs` : espId.toUpperCase();

    const genStartDate = customParams?.startDate || activeBaseDate;
    const genDays = customParams?.days || activeDays;
    const genPrompt = customParams?.prompt !== undefined ? customParams.prompt : userPrompt;

    // Open progress modal
    setProgressModal({
      open: true,
      title: isCountrySync ? `Generating ${targetGeo.toUpperCase()} Plan (${genDays} Days)` : `Generating ${genDays}-Day Plan`,
      subtitle: isCountrySync 
        ? `Coordinating ${genDays}-day schedule across all ${targetGeo.toUpperCase()} ESPs in parallel`
        : `Optimizing ${genDays}-day schedule for ${displayTarget}`,
      stage: 'processing',
      progress: 15,
      currentMessage: isCountrySync
        ? 'Analyzing 30-day send history & scoring variants across all ESP channels...'
        : 'Analyzing 30-day send history & advertiser scoring...',
    });

    let prog = 18;
    const interval = setInterval(() => {
      prog = Math.min(prog + Math.floor(Math.random() * 8) + 4, 90);
      setProgressModal(prev => ({
        ...prev,
        progress: prog,
        currentMessage: isCountrySync
          ? (prog < 50 ? 'Staggering secondary ESPs around FeedBlitz to avoid brand collisions...' : 'Finalizing variant allocations and anti-fatigue checks...')
          : (prog < 45 ? 'Applying anti-fatigue & vertical rotation rules...' : 'Selecting highest expected conversion variants...'),
      }));
    }, 400);

    try {
      const payload: any = {
        prompt: genPrompt,
        weekId,
        startDate: genStartDate,
        days: genDays
      };

      if (isCountrySync) {
        payload.geo = targetGeo;
        payload.espId = espId;
      } else {
        payload.espId = espId;
      }

      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      clearInterval(interval);
      const data = await res.json();
      if (data.error) {
        setGenerateResult(`❌ Error: ${data.error}`);
        setProgressModal(prev => ({
          ...prev,
          stage: 'error',
          errorMsg: data.error,
        }));
      } else {
        const total = data.totalPending || 0;
        const dryRun = data.dryRun ? ' [DRY RUN]' : '';
        if (data.weekId) {
          setWeekId(data.weekId);
        }
        setGenerateResult(`✅ Generated ${total} pending drafts for ${data.weekId || genStartDate}${dryRun}`);
        setProgressModal({
          open: true,
          title: 'Draft Plan Generated!',
          subtitle: isCountrySync 
            ? `${total} draft campaign(s) planned across all ${targetGeo.toUpperCase()} ESPs`
            : `${total} draft campaign(s) created for ${espId.toUpperCase()}`,
          stage: 'completed',
          progress: 100,
          currentMessage: `Generated ${total} draft slots (Pending Approval)`,
          completedCount: total,
          totalCount: total,
          resultsSummary: { success: total, failed: 0, message: `${total} draft campaign(s) added to plan — Review & approve before pushing to ESP` }
        });

        await fetchWeekPlan();
      }
    } catch (err: any) {
      clearInterval(interval);
      setGenerateResult(`❌ ${err.message}`);
      setProgressModal(prev => ({
        ...prev,
        stage: 'error',
        errorMsg: err.message,
      }));
    } finally {
      setGenerating(false);
    }
  }, [espId, activeBaseDate, activeDays, userPrompt, weekId, fetchWeekPlan]);

  const handleApproveAll = useCallback(async () => {
    if (!espId) return;
    
    // Open progress modal
    setProgressModal({
      open: true,
      title: 'Approving All Campaigns',
      subtitle: `Running safety audit & setting status to approved`,
      stage: 'validating',
      progress: 25,
      currentMessage: 'Running 5-point quality and compliance validation...',
    });

    try {
      setLoading(true);
      // Run pre-approval quality & safety validation
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/validate-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ espId, weekId, startDate: activeBaseDate, days: activeDays }),
      });
      if (res.ok) {
        const valData = await res.json();
        if (!valData.allValid && valData.issueCount > 0) {
          setProgressModal(prev => ({ ...prev, open: false }));
          setAuditReport(valData);
          setAuditModalOpen(true);
          return;
        }
      }
      
      setProgressModal(prev => ({
        ...prev,
        stage: 'processing',
        progress: 65,
        currentMessage: 'Validation passed! Marking campaigns as approved (ready)...',
      }));

      // If 100% valid, proceed directly with approval
      const actRes = await fetch(`${API_BASE_URL}/api/auto-schedule/approve-week`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ espId, weekId, startDate: activeBaseDate, days: activeDays })
      });
      const actData = await actRes.json();
      const approvedCount = actData.approved ?? weekPlan?.items?.filter(i => i.status === 'pending').length ?? 0;
      
      setProgressModal({
        open: true,
        title: 'All Campaigns Approved!',
        subtitle: `${approvedCount} campaign(s) ready for scheduling`,
        stage: 'completed',
        progress: 100,
        currentMessage: 'Campaigns are approved and ready to be pushed to ESP.',
        completedCount: approvedCount,
        totalCount: approvedCount,
        resultsSummary: { success: approvedCount, failed: 0 }
      });

      await Promise.all([fetchWeekPlan(), fetchAlreadyScheduled(), fetchToday()]);
    } catch (e: any) {
      setProgressModal(prev => ({
        ...prev,
        stage: 'error',
        errorMsg: e.message || 'Validation error occurred',
      }));
    } finally {
      setLoading(false);
    }
  }, [espId, weekId, activeBaseDate, activeDays, weekPlan, fetchWeekPlan, fetchAlreadyScheduled, fetchToday]);

  const handleAutoRepairAll = useCallback(async () => {
    if (!espId) return;
    
    setProgressModal({
      open: true,
      title: 'Auto-Repairing & Approving',
      subtitle: `Re-rendering missing content directly from Text Library`,
      stage: 'processing',
      progress: 30,
      currentMessage: 'Rebuilding responsive templates and compliance boxes...',
    });

    try {
      setIsRepairing(true);
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/auto-repair`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ espId, weekId, startDate: activeBaseDate, days: activeDays }),
      });
      if (!res.ok) throw new Error('Auto-repair failed');
      
      setProgressModal(prev => ({
        ...prev,
        progress: 75,
        currentMessage: 'Repair successful. Approving valid campaigns...',
      }));

      // Re-validate and approve
      await handleAction('approve-week', { espId, weekId, startDate: activeBaseDate, days: activeDays });
      setAuditModalOpen(false);
      setAuditReport(null);
      
      setProgressModal({
        open: true,
        title: 'Repair & Approval Complete',
        stage: 'completed',
        progress: 100,
        currentMessage: 'All campaigns repaired from library and approved.',
      });

      await fetchWeekPlan();
    } catch (e: any) {
      setProgressModal(prev => ({
        ...prev,
        stage: 'error',
        errorMsg: e.message || 'Auto-repair error',
      }));
    } finally {
      setIsRepairing(false);
    }
  }, [espId, weekId, activeBaseDate, activeDays, handleAction, fetchWeekPlan]);

  const handleAutoRepairSingle = useCallback(async (afimaCampaignId: string) => {
    if (!afimaCampaignId) return;
    try {
      setIsRepairing(true);
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/auto-repair`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ afimaCampaignId }),
      });
      if (!res.ok) throw new Error('Repair failed');
      // Refresh audit report
      const valRes = await fetch(`${API_BASE_URL}/api/auto-schedule/validate-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ espId, weekId, startDate: activeBaseDate, days: activeDays }),
      });
      if (valRes.ok) {
        const valData = await valRes.json();
        setAuditReport(valData);
        if (valData.allValid) {
          setAuditModalOpen(false);
        }
      }
      await fetchWeekPlan();
    } catch (e: any) {
      alert(`Repair error: ${e.message}`);
    } finally {
      setIsRepairing(false);
    }
  }, [espId, weekId, activeBaseDate, activeDays, fetchWeekPlan]);

  const handleRegenerate = () => {
    if (window.confirm('Regenerate the plan with AI? All pending draft suggestions will be replaced.')) {
      setProgressModal({
        open: true,
        title: 'Regenerating AI Plan',
        subtitle: `Replacing pending drafts with fresh angle rotation`,
        stage: 'processing',
        progress: 20,
        currentMessage: 'Clearing pending drafts & querying campaign scoring engine...',
      });
      handleGenerate();
    }
  };

  const handleClearPlan = useCallback(async () => {
    if (!window.confirm('Clear all pending and un-sent draft suggestions for this period? (Scheduled and approved campaigns will be preserved)')) return;
    try {
      setLoading(true);
      const startD = displayDates[0] || activeBaseDate;
      const endD = displayDates[displayDates.length - 1] || activeBaseDate;
      const itemIds = (weekPlan?.items || [])
        .filter((i: any) => !i.espCampaignId && !i.newsletterId && i.status !== 'sent' && i.status !== 'dispatched')
        .map((i: any) => i.afimaCampaignId)
        .filter(Boolean);

      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/clear-week`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          espId,
          weekId,
          startDate: startD,
          endDate: endD,
          days: activeDays,
          campaignIds: itemIds
        })
      });
      if (!res.ok) throw new Error('Clear failed');
      const data = await res.json();
      setGenerateResult(`🗑️ Cleared ${data.deleted || 0} pending draft items. Scheduled campaigns preserved.`);
      await Promise.all([fetchWeekPlan(), fetchAlreadyScheduled(), fetchToday()]);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  }, [espId, weekId, activeBaseDate, activeDays, displayDates, weekPlan, fetchWeekPlan, fetchAlreadyScheduled, fetchToday]);

  const handleScheduleAllNow = useCallback(async () => {
    if (!weekPlan?.items || weekPlan.items.length === 0) return;
    
    // STRICT: ONLY dispatch approved campaigns! Never push unapproved pending drafts
    const approvedOnly = weekPlan.items.filter(i => i.status === 'approved');
    if (approvedOnly.length === 0) {
      alert('⚠️ No approved campaigns found to push.\n\nPlease review and click "Approve" on individual cards (or "Select Pending" -> "Approve Selected") before pushing to ESP.');
      return;
    }
    const candidates = approvedOnly;

    // Filter out campaigns whose scheduled time is in the past
    const now = Date.now();
    const toDispatch = candidates.filter(i => {
      const dStr = i.scheduledDate || (i as any).date;
      if (!dStr) return false;
      const tStr = i.slotTime || (dStr.includes('T') ? dStr.split('T')[1].slice(0, 5) : '10:00');
      const datePart = dStr.slice(0, 10);
      const itemDt = new Date(`${datePart}T${tStr}:00`);
      return itemDt.getTime() > now - 2 * 60 * 1000;
    });

    const pastCount = candidates.length - toDispatch.length;
    if (toDispatch.length === 0) {
      alert(`⚠️ Cannot schedule in the past: All ${candidates.length} draft/pending campaign(s) are set for dates or times that have already passed. Please update their times before pushing.`);
      return;
    }

    const isApprovedOnly = approvedOnly.length > 0;
    const skippedPending = isApprovedOnly ? (weekPlan.items.filter(i => i.status === 'pending').length) : 0;
    const pendingMsg = skippedPending > 0 ? ` (${skippedPending} unapproved draft(s) will remain pending)` : '';
    const pastWarning = pastCount > 0 ? `\n\n⚠️ ${pastCount} campaign(s) scheduled for past times will be skipped.` : '';
    const confirm = window.confirm(`🚀 Are you sure you want to pre-schedule ${toDispatch.length} ${isApprovedOnly ? 'APPROVED' : ''} campaign(s) directly into ESP right now?${pendingMsg}${pastWarning}`);
    if (!confirm) return;

    // Open animated progress modal
    setProgressModal({
      open: true,
      title: `Pre-Scheduling ${toDispatch.length} Campaign(s)`,
      subtitle: `Directly dispatching to ${espId.toUpperCase()} API`,
      stage: 'dispatching',
      progress: 12,
      currentMessage: 'Connecting to ESP API & validating list permissions...',
      completedCount: 0,
      totalCount: toDispatch.length,
    });

    // Start smooth progress animation
    let prog = 15;
    const interval = setInterval(() => {
      prog = Math.min(prog + Math.floor(Math.random() * 7) + 3, 90);
      const completed = Math.min(Math.floor((prog / 100) * toDispatch.length), toDispatch.length - 1);
      setProgressModal(prev => ({
        ...prev,
        progress: prog,
        completedCount: completed,
        currentMessage: prog < 40 
          ? 'Uploading responsive HTML templates & tracking redirects...' 
          : prog < 75 
            ? `Scheduling slots (${completed}/${toDispatch.length}) & configuring segmentation...` 
            : 'Finalizing ESP delivery timestamps & verifying IDs...',
      }));
    }, 450);

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/dispatch-week`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          espId, 
          weekId, 
          startDate: activeBaseDate, 
          days: activeDays,
          campaignIds: toDispatch.map(i => i.afimaCampaignId)
        }),
      });
      clearInterval(interval);
      const data = await res.json();
      if (res.ok && !data.error) {
        const count = data.dispatched ?? data.total ?? toDispatch.length;
        const failedCount = data.failed ?? 0;
        const dryRunMsg = data.dryRun ? ' (DRY RUN mode)' : '';
        const isAllFailed = count === 0 && failedCount > 0;
        
        setProgressModal({
          open: true,
          title: isAllFailed ? 'Scheduling Failed ⚠️' : (failedCount > 0 ? 'Scheduling Completed with Warnings ⚠️' : 'Scheduling Complete! 🚀'),
          subtitle: isAllFailed ? `Failed to schedule in ${espId.toUpperCase()}` : `Successfully scheduled in ${espId.toUpperCase()}${dryRunMsg}`,
          stage: isAllFailed ? 'error' : 'completed',
          progress: 100,
          currentMessage: isAllFailed ? `0 of ${failedCount} campaign(s) scheduled in ESP.` : `All ${count} campaign(s) are now scheduled live in ESP!`,
          completedCount: count,
          totalCount: toDispatch.length,
          resultsSummary: { success: count, failed: failedCount, message: `${count} campaign(s) scheduled directly in ESP` },
          errorMsg: isAllFailed ? (data.results?.find((r: any) => r.error)?.error || 'All campaigns failed to schedule') : undefined,
        });

        await fetchWeekPlan();
      } else {
        setProgressModal(prev => ({
          ...prev,
          stage: 'error',
          errorMsg: data.error || `HTTP ${res.status}: Failed to schedule campaigns`,
        }));
      }
    } catch (e: any) {
      clearInterval(interval);
      setProgressModal(prev => ({
        ...prev,
        stage: 'error',
        errorMsg: e.message || 'Network error occurred during dispatch',
      }));
    } finally {
      setLoading(false);
    }
  }, [espId, weekId, activeBaseDate, activeDays, weekPlan, fetchWeekPlan]);

  const handleMoveItem = useCallback(async (item: PendingItem, newDate: string, newTime?: string) => {
    if (!item || !newDate) return;
    const time = newTime || item.slotTime || '08:00';
    const oldDate = item.scheduledDate ? item.scheduledDate.slice(0, 10) : '';
    if (oldDate === newDate && (!newTime || newTime === item.slotTime)) return;

    // Optimistic UI update
    setWeekPlan(prev => {
      if (!prev) return prev;
      const updatedItems = prev.items.map(it => {
        if (it.afimaCampaignId === item.afimaCampaignId) {
          return { ...it, scheduledDate: `${newDate}T${time}:00.000Z`, slotTime: time };
        }
        return it;
      });
      const byDate: Record<string, PendingItem[]> = {};
      updatedItems.forEach(it => {
        const d = it.scheduledDate ? it.scheduledDate.slice(0, 10) : '';
        if (!byDate[d]) byDate[d] = [];
        byDate[d].push(it);
      });
      return { ...prev, items: updatedItems, byDate };
    });

    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          afimaCampaignId: item.afimaCampaignId,
          newDate,
          newTime: time,
        }),
      });
      if (!res.ok) throw new Error('Move failed');
      const data = await res.json();
      if (data.error) throw new Error(data.error);
    } catch (err: any) {
      console.error('Failed to persist move:', err);
      fetchWeekPlan();
    }
  }, [fetchWeekPlan]);

  const handleSwapItems = useCallback(async (itemA: PendingItem, itemB: PendingItem) => {
    if (!itemA || !itemB || itemA.afimaCampaignId === itemB.afimaCampaignId) return;

    const dateA = itemA.scheduledDate;
    const timeA = itemA.slotTime;
    const idxA = itemA.slotIndex;
    const nlA = itemA.includeNL;

    const dateB = itemB.scheduledDate;
    const timeB = itemB.slotTime;
    const idxB = itemB.slotIndex;
    const nlB = itemB.includeNL;

    // Optimistic UI update
    setWeekPlan(prev => {
      if (!prev) return prev;
      const updatedItems = prev.items.map(it => {
        if (it.afimaCampaignId === itemA.afimaCampaignId) {
          return { ...it, scheduledDate: dateB, slotTime: timeB, slotIndex: idxB, includeNL: nlB };
        }
        if (it.afimaCampaignId === itemB.afimaCampaignId) {
          return { ...it, scheduledDate: dateA, slotTime: timeA, slotIndex: idxA, includeNL: nlA };
        }
        return it;
      });
      const byDate: Record<string, PendingItem[]> = {};
      updatedItems.forEach(it => {
        const d = it.scheduledDate ? it.scheduledDate.slice(0, 10) : '';
        if (!byDate[d]) byDate[d] = [];
        byDate[d].push(it);
      });
      return { ...prev, items: updatedItems, byDate };
    });

    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/swap-items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceId: itemA.afimaCampaignId,
          targetId: itemB.afimaCampaignId,
        }),
      });
      if (!res.ok) throw new Error('Swap failed');
      const data = await res.json();
      if (data.error) throw new Error(data.error);
    } catch (err: any) {
      console.error('Failed to persist swap:', err);
      fetchWeekPlan();
    }
  }, [fetchWeekPlan]);

  const handleReplaceSlotCampaign = useCallback(async (afimaCampaignId: string, campaignName: string, textEdition: string) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/replace-campaign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          afimaCampaignId,
          campaignName,
          textEdition,
        }),
      });
      if (!res.ok) throw new Error('Replace failed');
      await fetchWeekPlan();
    } catch (err: any) {
      alert(`Replace failed: ${err.message}`);
    } finally {
      setLoading(false);
      setSwappingItem(null);
    }
  }, [fetchWeekPlan]);

  const getStatusColors = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'approved': return 'bg-teal-100 text-teal-800 border-teal-300';
      case 'dispatched': return 'bg-sky-100 text-sky-800 border-sky-300';
      case 'sent': return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-300';
      case 'held': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'failed': return 'bg-rose-200 text-rose-900 border-rose-400';
      case 'manual': return 'bg-slate-100 text-slate-700 border-slate-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'bg-emerald-500';
    if (score >= 40) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const planStats = useMemo(() => {
    const items = weekPlan?.items || [];
    const now = new Date();

    let pending = 0;
    let approved = 0;
    let preScheduled = 0;
    let sent = 0;
    let rejectedOrHeld = 0;

    items.forEach(item => {
      const isPast = item.scheduledDate ? new Date(item.scheduledDate) <= now : false;
      const isItemSent = item.status === 'sent' || (item.status === 'dispatched' && isPast);

      if (isItemSent) {
        sent++;
      } else if (item.status === 'dispatched' || item.status === 'scheduled') {
        preScheduled++;
      } else if (item.status === 'approved') {
        approved++;
      } else if (item.status === 'pending') {
        pending++;
      } else if (item.status === 'held' || item.status === 'rejected') {
        rejectedOrHeld++;
      }
    });

    // Also include external/manual campaigns from alreadyScheduled that are not in weekPlan
    const planItemIds = new Set(items.map(i => i.afimaCampaignId).filter(Boolean));
    const planNames = new Set(items.map(i => (i.campaignName || '').toLowerCase().trim()));
    alreadyScheduled.forEach(s => {
      if (s.scheduledId && planItemIds.has(s.scheduledId)) return;
      if (s.afimaCampaignId && planItemIds.has(s.afimaCampaignId)) return;
      const sName = (s.name || s.campaignName || '').toLowerCase().trim();
      if (sName && planNames.has(sName)) return;
      const isPast = s.sendOn || s.date ? new Date(s.sendOn || s.date) <= now : false;
      const isItemSent = s.status === 'sent' || isPast;
      if (isItemSent) {
        sent++;
      } else {
        preScheduled++;
      }
    });

    const totalCalculated = pending + approved + preScheduled + sent + rejectedOrHeld;

    return {
      pending,
      approved,
      preScheduled,
      sent,
      rejectedOrHeld,
      total: totalCalculated || (weekPlan?.summary?.total ?? 0)
    };
  }, [weekPlan, alreadyScheduled]);

  const hasDryRun = useMemo(() => {
    return weekPlan?.items?.some(i => i.dryRun) || todayDispatches.some(i => i.dryRun);
  }, [weekPlan, todayDispatches]);

  const dayNames = ['Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Mon'];

  return (
    <MainLayout>
      <div className="space-y-6 pb-12">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 shadow-md text-white flex flex-col gap-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 opacity-90" />
              <h1 className="text-2xl font-bold tracking-tight">Auto-Schedule</h1>
              <div className="bg-black/25 p-1 rounded-xl border border-white/20 flex items-center gap-1 ml-1">
                <button
                  type="button"
                  onClick={() => setMainTab('planner')}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                    mainTab === 'planner'
                      ? 'bg-white text-indigo-800 shadow-xs'
                      : 'text-white/85 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Calendar className="w-3.5 h-3.5" />
                  Weekly Planner
                </button>
                <button
                  type="button"
                  onClick={() => setMainTab('intelligence')}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                    mainTab === 'intelligence'
                      ? 'bg-white text-indigo-800 shadow-xs'
                      : 'text-white/85 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  🧠 Scheduler Intelligence
                </button>
              </div>
              <span className="px-3 py-1 bg-white/20 text-white text-xs font-semibold rounded-full flex items-center gap-1.5 border border-white/20">
                <Clock className="w-3.5 h-3.5" /> 🇩🇰 Europe/Copenhagen (CEST / UTC+2)
              </span>
              {hasDryRun && (
                <span className="ml-3 px-3 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full uppercase tracking-wider flex items-center gap-1 shadow-sm">
                  <AlertTriangle className="w-3 h-3" /> Dry Run Active
                </span>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2.5 flex-wrap justify-end">
                {/* View Switcher: 7d | 14d | Month */}
                <div className="bg-white/15 p-0.5 rounded-lg border border-white/25 flex items-center shadow-xs">
                  <button
                    type="button"
                    onClick={() => setViewMode('7d')}
                    className={`px-2.5 py-1 text-xs font-bold rounded-md transition-all cursor-pointer ${
                      viewMode === '7d'
                        ? 'bg-white text-indigo-700 shadow-xs'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    7 Days
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('14d')}
                    className={`px-2.5 py-1 text-xs font-bold rounded-md transition-all cursor-pointer ${
                      viewMode === '14d'
                        ? 'bg-white text-indigo-700 shadow-xs'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    14 Days
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('month')}
                    className={`px-2.5 py-1 text-xs font-bold rounded-md transition-all cursor-pointer ${
                      viewMode === 'month'
                        ? 'bg-white text-indigo-700 shadow-xs'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    Month (28d)
                  </button>
                </div>

                {/* ESP Selector */}
                <div className="relative flex items-center">
                  <select 
                    value={espId} 
                    onChange={(e) => {
                      const newEsp = e.target.value;
                      setEspId(newEsp);
                      setWeekPlan(null);
                      setAlreadyScheduled([]);
                      setSelectedCampaigns(new Map());
                    }}
                    className={`bg-white/20 border border-white/30 text-white rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-white/50 [&>option]:text-gray-900 [&>optgroup]:text-gray-900 font-medium cursor-pointer transition-all ${
                      loading ? 'pr-8 ring-2 ring-white/40' : ''
                    }`}
                  >
                    <optgroup label="🌍 Sync Whole Country (All ESPs)">
                      <option value="all_se">🇸🇪 All Sweden (FeedBlitz, GetResponse, Moosend)</option>
                      <option value="all_no">🇳🇴 All Norway (FeedBlitz, GetResponse)</option>
                      <option value="all_fi">🇫🇮 All Finland (FeedBlitz, GR, ML, Brevo)</option>
                      <option value="all_de">🇩🇪 All Germany (FeedBlitz DE)</option>
                    </optgroup>
                    <optgroup label="SimplyLoan (Brevo)">
                      <option value="all_sl">⚡ All SimplyLoan (SE, NO, FI)</option>
                      <option value="br_sl_se">SimplyLoan Sweden (SE)</option>
                      <option value="br_sl_no">SimplyLoan Norway (NO)</option>
                      <option value="br_sl_fi">SimplyLoan Finland (FI)</option>
                    </optgroup>
                    <optgroup label="FeedBlitz">
                      <option value="feedblitz_se">FeedBlitz Sweden</option>
                      <option value="feedblitz_no">FeedBlitz Norway</option>
                      <option value="feedblitz_fi">FeedBlitz Finland</option>
                      <option value="feedblitz_de">FeedBlitz Germany</option>
                    </optgroup>
                    <optgroup label="GetResponse">
                      <option value="gr_se">GetResponse Sweden (Sparkredit)</option>
                      <option value="gr_se2">GetResponse Sweden (Svenska Lån)</option>
                      <option value="gr_no">GetResponse Norway (Norske Tilbud)</option>
                      <option value="gr_fi">GetResponse Finland (Suomalaisetlainat)</option>
                    </optgroup>
                    <optgroup label="Moosend">
                      <option value="ms_se">Moosend Sweden (Swiftkredit)</option>
                    </optgroup>
                    <optgroup label="Drip">
                      <option value="dp_se">Drip Sweden (Enkelt Lån)</option>
                    </optgroup>
                    <optgroup label="MailerLite">
                      <option value="ml_fi">MailerLite Finland (Tarjouksesi)</option>
                    </optgroup>
                    <optgroup label="Brevo">
                      <option value="br_fi">Brevo Finland (Lainatsuomessa)</option>
                    </optgroup>
                  </select>
                  {loading && (
                    <div className="absolute right-2.5 pointer-events-none flex items-center">
                      <RefreshCw className="w-3.5 h-3.5 text-white animate-spin" />
                    </div>
                  )}
                </div>

                {/* Add Campaign */}
                <button 
                  onClick={() => {
                    const todayStr = formatYYYYMMDD(new Date());
                    setManualAddDate(todayStr);
                    setManualAddOpen(true);
                  }}
                  title="Manually add a campaign"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-3.5 py-1.5 rounded-lg transition-all duration-200 shadow flex items-center gap-1.5 text-xs"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Campaign
                </button>

                {/* Generate Plan Button */}
                <button 
                  onClick={() => setGenerateModalOpen(true)}
                  disabled={generating || loading || syncLoading}
                  className="bg-white text-indigo-700 hover:bg-indigo-50 font-bold px-4 py-1.5 rounded-lg transition-all duration-200 shadow flex items-center gap-1.5 text-xs disabled:opacity-50"
                >
                  <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                  Generate Plan
                </button>

                {/* Sync ESP */}
                <button 
                  onClick={handleSyncEsp}
                  disabled={syncLoading || loading || generating}
                  title="Sync live scheduled campaigns with ESP and purge deleted ones"
                  className="bg-indigo-700/90 hover:bg-indigo-600 text-white font-medium px-3 py-1.5 rounded-lg transition-all duration-200 border border-white/20 shadow flex items-center gap-1 disabled:opacity-50 text-xs"
                >
                  <RefreshCw className={`w-3 h-3 ${syncLoading ? 'animate-spin' : ''}`} />
                  {syncLoading ? 'Syncing...' : 'Sync ESP'}
                </button>
              </div>

              <button 
                onClick={() => setShowPrompt(!showPrompt)}
                className="text-white/80 hover:text-white text-xs font-medium transition-colors underline underline-offset-2"
              >
                {showPrompt ? 'Hide AI Instructions' : '+ AI Custom Instructions'}
              </button>
            </div>
          </div>
          {showPrompt && (
            <div className="pt-2 border-t border-white/20 mt-2">
              <input 
                type="text" 
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                placeholder="e.g. Don't include Axo this week. Send Sambla at least twice. Prioritize campaigns not sent in 20+ days."
                className="w-full bg-white/10 text-white placeholder-white/50 border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>
          )}
        </div>

        {generateResult && (
          <div className={`p-4 rounded-lg flex items-center gap-2 border text-sm font-medium ${
            generateResult.startsWith('✅') ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
            generateResult.startsWith('⚠️') ? 'bg-amber-50 text-amber-800 border-amber-200' :
            'bg-red-50 text-red-700 border-red-200'
          }`}>
            <Info className="w-5 h-5 flex-shrink-0" />
            <span>{generateResult}</span>
            <button onClick={() => setGenerateResult(null)} className="ml-auto text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-center gap-2 border border-red-200">
            <AlertTriangle className="w-5 h-5" /> {error}
          </div>
        )}

        {mainTab === 'intelligence' ? (
          <SchedulerIntelligenceView selectedEspId={espId} onSelectEspId={setEspId} />
        ) : (
        <>
        {/* Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <Card className="border-l-4 border-l-amber-500 shadow-xs hover:shadow-sm transition-all duration-200">
            <CardContent className="p-3.5 flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Pending</p>
                <p className="text-xl font-bold text-gray-900">{planStats.pending}</p>
              </div>
              <Clock className="w-7 h-7 text-amber-300" />
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-teal-500 shadow-xs hover:shadow-sm transition-all duration-200">
            <CardContent className="p-3.5 flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Approved</p>
                <p className="text-xl font-bold text-gray-900">{planStats.approved}</p>
              </div>
              <CheckCircle2 className="w-7 h-7 text-teal-300" />
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-sky-500 shadow-xs hover:shadow-sm transition-all duration-200">
            <CardContent className="p-3.5 flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Pre-Scheduled</p>
                <p className="text-xl font-bold text-gray-900">{planStats.preScheduled}</p>
              </div>
              <Send className="w-7 h-7 text-sky-300" />
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500 shadow-xs hover:shadow-sm transition-all duration-200">
            <CardContent className="p-3.5 flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</p>
                <p className="text-xl font-bold text-emerald-700">{planStats.sent}</p>
              </div>
              <Check className="w-7 h-7 text-emerald-300" />
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-rose-400 shadow-xs hover:shadow-sm transition-all duration-200">
            <CardContent className="p-3.5 flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Rejected / Held</p>
                <p className="text-xl font-bold text-gray-900">{planStats.rejectedOrHeld}</p>
              </div>
              <XCircle className="w-7 h-7 text-rose-300" />
            </CardContent>
          </Card>
        </div>

        {/* Weekly/Monthly Calendar */}
        <Card className="shadow-sm">
          <CardHeader className="bg-gray-50/80 border-b p-3 flex flex-col md:flex-row md:items-center justify-between gap-3">
            {/* Left: Date Horizon & Navigation Arrows */}
            <div className="flex items-center gap-2.5 flex-wrap">
              {/* Prev / Today / Next Arrows */}
              <div className="flex items-center bg-white border border-gray-200 rounded-lg shadow-2xs p-0.5">
                <button
                  type="button"
                  onClick={handlePrev}
                  title="Previous period"
                  className="p-1.5 hover:bg-gray-100 rounded text-gray-600 transition-colors cursor-pointer"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={handleToday}
                  title="Jump to current week"
                  className="px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  title="Next period"
                  className="p-1.5 hover:bg-gray-100 rounded text-gray-600 transition-colors cursor-pointer"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Range Horizon Badge */}
              <div className="px-3 py-1 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-800 shadow-2xs flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                <span>
                  {formatDateLabel(displayDates[0] || '').label} – {formatDateLabel(displayDates[displayDates.length - 1] || '').label}
                </span>
                <span className="text-gray-400 font-normal">({activeDays} Days)</span>
              </div>

              {/* Jump to Specific Date / Preset */}
              <div className="flex items-center bg-white border border-gray-200 rounded-lg shadow-2xs px-2 py-1">
                <select
                  value={activeBaseDate}
                  onChange={(e) => setBaseStartDate(e.target.value)}
                  className="bg-transparent text-gray-700 text-xs border-0 focus:outline-none font-medium cursor-pointer"
                >
                  {datePresets.map((opt, idx) => (
                    <option key={idx} value={opt.start}>{opt.label}</option>
                  ))}
                </select>
                <input
                  type="date"
                  value={activeBaseDate}
                  onChange={(e) => setBaseStartDate(e.target.value)}
                  title="Pick custom date"
                  className="text-xs text-gray-600 border-l border-gray-200 pl-2 ml-1 focus:outline-none cursor-pointer"
                />
              </div>
            </div>

            {/* Right: Multi-select & Batch Actions */}
            {displayDates.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => {
                    const all = new Map();
                    weekPlan?.items?.forEach(it => {
                      if (isSlView ? !isSlItem(it) : isSlItem(it)) return;
                      const k = it.afimaCampaignId || it.scheduledId || it.id;
                      if (k && displayDates.includes(it.scheduledDate?.slice(0, 10))) all.set(k, it);
                    });
                    displayDates.forEach(dateStr => {
                      const sched = alreadyScheduled.filter((s: any) => s.date === dateStr && (isSlView ? isSlItem(s) : !isSlItem(s)));
                      sched.forEach((s: any, idx: number) => {
                        const k = s.scheduledId || s.id || s.newsletterId || `manual-${s.name}-${dateStr}-${idx}`;
                        all.set(k, { ...s, scheduledId: s.scheduledId || s.id || s.newsletterId, campaignName: s.name, key: k });
                      });
                    });
                    setSelectedCampaigns(all);
                  }}
                  className="px-2.5 py-1 text-xs font-medium text-gray-600 hover:text-indigo-600 bg-white hover:bg-indigo-50 border border-gray-200 rounded-md transition-all shadow-2xs cursor-pointer"
                  title="Select all campaigns in this view"
                >
                  Select All
                </button>
                <button
                  onClick={() => {
                    const preSched = new Map();
                    weekPlan?.items?.forEach(it => {
                      if (isSlView ? !isSlItem(it) : isSlItem(it)) return;
                      if ((it.status === 'dispatched' || it.status === 'scheduled') && displayDates.includes(it.scheduledDate?.slice(0, 10))) {
                        const k = it.afimaCampaignId || it.scheduledId || it.id;
                        if (k) preSched.set(k, it);
                      }
                    });
                    displayDates.forEach(dateStr => {
                      const sched = alreadyScheduled.filter((s: any) => s.date === dateStr && (isSlView ? isSlItem(s) : !isSlItem(s)));
                      sched.forEach((s: any, idx: number) => {
                        if (s.status !== 'sent') {
                          const k = s.scheduledId || s.id || s.newsletterId || `manual-${s.name}-${dateStr}-${idx}`;
                          preSched.set(k, { ...s, scheduledId: s.scheduledId || s.id || s.newsletterId, campaignName: s.name, key: k });
                        }
                      });
                    });
                    setSelectedCampaigns(preSched);
                  }}
                  className="px-2.5 py-1 text-xs font-medium text-sky-700 hover:text-sky-800 bg-sky-50 hover:bg-sky-100 border border-sky-200 rounded-md transition-all shadow-2xs cursor-pointer"
                  title="Select all pre-scheduled campaigns"
                >
                  Select Pre-Scheduled
                </button>
                <button
                  onClick={() => {
                    const pending = new Map();
                    weekPlan?.items?.forEach(it => {
                      if (isSlView ? !isSlItem(it) : isSlItem(it)) return;
                      if (it.status === 'pending' && displayDates.includes(it.scheduledDate?.slice(0, 10))) {
                        const k = it.afimaCampaignId || it.scheduledId || it.id;
                        if (k) pending.set(k, it);
                      }
                    });
                    setSelectedCampaigns(pending);
                  }}
                  className="px-2.5 py-1 text-xs font-medium text-amber-700 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-md transition-all shadow-2xs cursor-pointer"
                  title="Select all pending campaigns"
                >
                  Select Pending
                </button>
                {selectedCampaigns.size > 0 && (
                  <>
                    <button
                      onClick={handleMultiUnschedule}
                      disabled={loading}
                      className="px-3 py-1 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md transition-all flex items-center gap-1.5 shadow-xs disabled:opacity-50 cursor-pointer"
                      title="Unschedule all selected campaigns"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Unschedule ({selectedCampaigns.size})
                    </button>
                    <button
                      onClick={() => setSelectedCampaigns(new Map())}
                      className="px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-all cursor-pointer"
                    >
                      Clear
                    </button>
                  </>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto relative min-h-[300px]">
            {/* ESP & Status Visual Legend */}
            <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-2 bg-slate-50 border-b border-gray-200 text-xs select-none">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-gray-500 text-[10px] uppercase tracking-wider">ESP Colors:</span>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-800 bg-sky-100/90 px-2 py-0.5 rounded border border-sky-200 shadow-2xs">
                  <span className="w-2 h-2 rounded-full bg-sky-500" /> FeedBlitz
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-800 bg-purple-100/90 px-2 py-0.5 rounded border border-purple-200 shadow-2xs">
                  <span className="w-2 h-2 rounded-full bg-purple-500" /> GetResponse
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-800 bg-indigo-100/90 px-2 py-0.5 rounded border border-indigo-200 shadow-2xs">
                  <span className="w-2 h-2 rounded-full bg-indigo-500" /> GR SE2
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-100/90 px-2 py-0.5 rounded border border-amber-200 shadow-2xs">
                  <span className="w-2 h-2 rounded-full bg-amber-500" /> SimplyLoan
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-800 bg-rose-100/90 px-2 py-0.5 rounded border border-rose-200 shadow-2xs">
                  <span className="w-2 h-2 rounded-full bg-rose-500" /> Moosend
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-pink-800 bg-pink-100/90 px-2 py-0.5 rounded border border-pink-200 shadow-2xs">
                  <span className="w-2 h-2 rounded-full bg-pink-500" /> Drip
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-cyan-800 bg-cyan-100/90 px-2 py-0.5 rounded border border-cyan-200 shadow-2xs">
                  <span className="w-2 h-2 rounded-full bg-cyan-500" /> MailerLite
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 bg-emerald-100/90 px-2 py-0.5 rounded border border-emerald-200 shadow-2xs">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" /> Brevo
                </span>
              </div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="font-bold text-gray-500 text-[10px] uppercase tracking-wider">Status:</span>
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-700">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" /> Sent
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-700">
                  <span className="w-2 h-2 rounded-xs bg-sky-500" /> Pre-Scheduled
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-700">
                  <span className="w-2 h-2 rounded-full bg-teal-500 ring-2 ring-teal-200" /> Approved
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-700">
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" /> Pending
                </span>
              </div>
            </div>

            {/* 1. Full Skeleton & Loading state when changing ESP or initial load without plan */}
            {loading && !weekPlan ? (
              <div className="min-w-[1000px] p-8">
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="relative mb-4">
                    <div className="w-12 h-12 rounded-full border-3 border-indigo-100 border-t-indigo-600 animate-spin" />
                    <Calendar className="w-5 h-5 text-indigo-600 absolute inset-0 m-auto animate-pulse" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1">
                    Loading {ESP_LABELS[espId] || espId} Schedule...
                  </h3>
                  <p className="text-xs text-gray-500 max-w-sm">
                    Fetching plan suggestions, live ESP dispatches, and campaign compliance...
                  </p>
                </div>
                {/* 7-column pulsating skeleton */}
                <div className="grid grid-cols-7 gap-3 pt-2">
                  {displayDates.slice(0, 7).map((d) => (
                    <div key={d} className="bg-gray-50 border border-gray-100 rounded-xl p-3 space-y-2.5 animate-pulse">
                      <div className="flex justify-between items-center pb-1 border-b border-gray-100">
                        <div className="h-3.5 bg-gray-200 rounded w-10" />
                        <div className="h-3 bg-gray-200 rounded w-12" />
                      </div>
                      <div className="h-16 bg-white border border-gray-200/60 rounded-lg p-2 space-y-1.5 shadow-2xs">
                        <div className="h-3 bg-gray-200 rounded w-20" />
                        <div className="h-2.5 bg-gray-100 rounded w-16" />
                      </div>
                      <div className="h-16 bg-white border border-gray-200/60 rounded-lg p-2 space-y-1.5 shadow-2xs">
                        <div className="h-3 bg-gray-200 rounded w-24" />
                        <div className="h-2.5 bg-gray-100 rounded w-14" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : weekRows.length > 0 ? (
              <div className="relative divide-y divide-gray-200">
                {/* Subtle floating overlay when refreshing in the background */}
                {loading && (
                  <div className="absolute inset-0 z-30 bg-white/60 backdrop-blur-[1px] flex items-center justify-center transition-all">
                    <div className="bg-gray-900/90 text-white px-5 py-2.5 rounded-full shadow-xl flex items-center gap-2.5 text-xs font-semibold animate-pulse border border-white/10">
                      <RefreshCw className="w-4 h-4 text-indigo-400 animate-spin" />
                      <span>Loading schedule for {ESP_LABELS[espId] || espId}...</span>
                    </div>
                  </div>
                )}
                {weekRows.map((row) => (
                  <div key={row.weekNum} className="border-b last:border-b-0">
                    {weekRows.length > 1 && (
                      <div className={`px-4 py-1.5 border-y flex items-center justify-between text-xs font-bold ${
                        row.weekNum % 2 === 1 ? 'bg-indigo-50/70 border-indigo-100 text-indigo-900' : 'bg-purple-50/70 border-purple-100 text-purple-900'
                      }`}>
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                          <span>{row.title}</span>
                        </div>
                        <span className="text-gray-500 font-normal text-[11px]">
                          {(() => {
                            const pItems = weekPlan?.items?.filter(i => row.dates.includes(i.scheduledDate?.slice(0, 10)) && (isSlView ? isSlItem(i) : !isSlItem(i))) || [];
                            const pIds = new Set(pItems.map(i => i.afimaCampaignId).filter(Boolean));
                            const pNames = new Set(pItems.map(i => (i.campaignName || '').toLowerCase().trim()));
                            const sCount = alreadyScheduled.filter(s => row.dates.includes(s.date) && (isSlView ? isSlItem(s) : !isSlItem(s)) && !pIds.has(s.scheduledId) && !pNames.has((s.name || '').toLowerCase().trim())).length;
                            return pItems.length + sCount;
                          })()} campaign(s)
                        </span>
                      </div>
                    )}
                    <div className="min-w-[1000px] grid grid-cols-7">
                      {row.dates.map((dateStr) => {
                        const { day, label } = formatDateLabel(dateStr);
                        const isToday = dateStr === new Date().toISOString().slice(0, 10);
                        const rawPlanItems = weekPlan?.byDate?.[dateStr] || [];
                        const planItems = rawPlanItems.filter((p, idx, arr) => {
                          if (isSlView ? !isSlItem(p) : isSlItem(p)) return false;
                          return arr.findIndex(x => x.afimaCampaignId === p.afimaCampaignId) === idx;
                        });
                        const planIds = new Set(planItems.map(p => p.afimaCampaignId).filter(Boolean));
                        const planNames = new Set(planItems.map(p => (p.campaignName || '').toLowerCase().trim()));
                        const scheduledForDay = alreadyScheduled.filter((s, idx, arr) => {
                          if (s.date !== dateStr) return false;
                          if (isSlView ? !isSlItem(s) : isSlItem(s)) return false;
                          if (s.scheduledId && planIds.has(s.scheduledId)) return false;
                          if (s.afimaCampaignId && planIds.has(s.afimaCampaignId)) return false;
                          const sName = (s.name || s.campaignName || '').toLowerCase().trim();
                          if (sName && planNames.has(sName)) return false;
                          return arr.findIndex(x => (x.scheduledId || x.id || x.name) === (x.scheduledId || s.id || s.name)) === idx;
                        });
                        const isDragOverDay = dragOverDate === dateStr && !dragOverItemId;

                        return (
                          <div 
                            key={dateStr} 
                            className={`border-r last:border-r-0 hover:bg-gray-50/40 transition-colors duration-200 ${
                              viewMode === '7d' ? 'min-h-[280px]' : viewMode === '14d' ? 'min-h-[160px]' : 'min-h-[130px]'
                            } ${isToday ? 'bg-indigo-50/30' : ''} ${isDragOverDay ? 'bg-indigo-100/50 ring-2 ring-indigo-400 ring-inset' : ''}`}
                            onDragOver={(e) => {
                              e.preventDefault();
                              if (dragOverDate !== dateStr) setDragOverDate(dateStr);
                            }}
                            onDragLeave={() => {
                              if (dragOverDate === dateStr) setDragOverDate(null);
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              if (draggedItem) {
                                handleMoveItem(draggedItem, dateStr);
                                setDraggedItem(null);
                                setDragOverDate(null);
                                setDragOverItemId(null);
                              }
                            }}
                          >
                            <div className={`p-1.5 text-center border-b font-medium text-xs relative group flex items-center justify-between px-2 ${isToday ? 'bg-indigo-100 text-indigo-900 font-bold' : 'bg-gray-100/80 text-gray-700'}`}>
                              <span className="font-semibold">{day}</span>
                              <span className={`text-[11px] font-normal ${isToday ? 'text-indigo-700 font-semibold' : 'text-gray-500'}`}>{label}</span>
                              <button
                                onClick={() => { setManualAddDate(dateStr); setManualAddOpen(true); }}
                                title={`Add campaign to ${day} (${label})`}
                                className="opacity-0 group-hover:opacity-100 p-0.5 bg-white/90 hover:bg-white text-indigo-600 rounded shadow-2xs transition-all border border-indigo-200 cursor-pointer"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="p-1.5 space-y-1.5">
                              {/* Already-scheduled (manual) campaigns */}
                              {scheduledForDay.map((s: any, idx: number) => {
                                const sKey = s.scheduledId || s.id || s.newsletterId || `manual-${s.name}-${dateStr}-${idx}`;
                                const isSelected = selectedCampaigns.has(sKey);
                                const isPast = s.sendOn || s.date ? new Date(s.sendOn || s.date) <= new Date() : false;
                                const isSent = s.status === 'sent' || isPast;
                                const espTheme = getEspTheme(s.systemId || s.espId, espId);

                                if (viewMode !== '7d') {
                                  return (
                                    <div 
                                      key={`sched-${idx}`} 
                                      className={`p-1.5 rounded-md border text-xs shadow-2xs cursor-pointer group transition-all relative flex items-center justify-between gap-1 ${espTheme.cardBg} ${espTheme.cardBorder} ${espTheme.cardHover} ${
                                        isSelected ? 'ring-2 ring-indigo-500 border-indigo-400' : ''
                                      }`}
                                      onClick={() => { setSelectedCampaign(s); setIsManualCampaign(true); }}
                                    >
                                      <div className="flex items-center gap-1 min-w-0">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            toggleSelectCampaign({
                                              ...s,
                                              scheduledId: s.scheduledId || s.id || s.newsletterId,
                                              campaignName: s.name,
                                              key: sKey
                                            }, sKey);
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          className="w-3 h-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer flex-shrink-0"
                                        />
                                        <span className="font-bold text-[10px] text-gray-700 font-mono flex-shrink-0">
                                          {formatCopenhagenTime(s.time, s.sendOn || s.date)}
                                        </span>
                                        <span className="font-semibold text-gray-900 truncate text-[11px]" title={s.name}>
                                          {s.name}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-1.5 flex-shrink-0">
                                        {espId.startsWith('all_') && (
                                          <span className={`px-1 py-0.2 rounded text-[9px] font-bold ${espTheme.tagBg} ${espTheme.tagText} border ${espTheme.tagBorder}`}>
                                            {espTheme.name}
                                          </span>
                                        )}
                                        {isSent ? (
                                          <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-2xs" title="Status: Sent" />
                                        ) : (
                                          <span className="w-2 h-2 rounded-xs bg-sky-500 shadow-2xs" title="Status: Pre-Scheduled on ESP" />
                                        )}
                                      </div>
                                    </div>
                                  );
                                }

                                return (
                                  <div 
                                    key={`sched-${idx}`} 
                                    className={`p-2.5 rounded-lg border text-xs shadow-sm cursor-pointer group transition-all relative ${espTheme.cardBg} ${espTheme.cardBorder} ${espTheme.cardHover} ${
                                      isSelected ? 'ring-2 ring-indigo-500 border-indigo-400 shadow-md' : ''
                                    }`}
                                    onClick={() => { setSelectedCampaign(s); setIsManualCampaign(true); }}
                                  >
                                    <div className="flex items-center justify-between gap-1 mb-1.5">
                                      <div className="flex items-center gap-1.5 min-w-0">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            toggleSelectCampaign({
                                              ...s,
                                              scheduledId: s.scheduledId || s.id || s.newsletterId,
                                              campaignName: s.name,
                                              key: sKey
                                            }, sKey);
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer flex-shrink-0"
                                          title="Select for batch unschedule"
                                        />
                                        <span className="font-semibold text-gray-900 truncate" title={s.name}>{s.name}</span>
                                      </div>
                                      <div className="flex items-center gap-1.5 flex-shrink-0">
                                        {isSent ? (
                                          <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-2xs" title="Status: Sent" />
                                        ) : (
                                          <span className="w-2 h-2 rounded-xs bg-sky-500 shadow-2xs" title="Status: Pre-Scheduled on ESP" />
                                        )}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleUnscheduleCampaign(s);
                                          }}
                                          title="Delete / Unschedule from ESP"
                                          className="opacity-0 group-hover:opacity-100 hover:bg-red-100 text-gray-400 hover:text-red-700 p-0.5 rounded transition-all flex-shrink-0 cursor-pointer"
                                        >
                                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-1 text-xs">
                                      <div className="flex items-center gap-1.5">
                                        <Clock className="w-3.5 h-3.5 text-gray-500" />
                                        <span className="font-semibold text-gray-800">{formatCopenhagenTime(s.time, s.sendOn || s.date)}</span>
                                        <span className="text-[10px] text-gray-400 font-normal">CEST</span>
                                      </div>
                                      {espId.startsWith('all_') && (
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${espTheme.tagBg} ${espTheme.tagText} border ${espTheme.tagBorder}`}>
                                          {espTheme.name}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}

                              {/* Auto-scheduled campaign items */}
                              {planItems.length > 0 ? planItems.map((item, idx) => {
                                const isItemDragged = draggedItem?.afimaCampaignId === item.afimaCampaignId;
                                const isItemDragTarget = dragOverItemId === item.afimaCampaignId && !isItemDragged;
                                const itemKey = item.afimaCampaignId || item.scheduledId || item.id || `item-${item.campaignName}-${dateStr}-${idx}`;
                                const isSelected = selectedCampaigns.has(itemKey);
                                const isItemPast = item.scheduledDate ? new Date(item.scheduledDate) <= new Date() : false;
                                const isItemSent = item.status === 'sent' || (item.status === 'dispatched' && isItemPast);
                                const isItemScheduled = (item.status === 'dispatched' || item.status === 'scheduled') && !isItemSent;
                                const isItemApproved = item.status === 'approved';
                                const isItemPending = item.status === 'pending';
                                const espTheme = getEspTheme(item.systemId, espId);

                                if (viewMode !== '7d') {
                                  return (
                                    <div 
                                      key={item.afimaCampaignId} 
                                      draggable={item.status === 'pending' || item.status === 'approved'}
                                      onDragStart={(e) => {
                                        e.dataTransfer.setData('text/plain', item.afimaCampaignId);
                                        setDraggedItem(item);
                                      }}
                                      onDragEnd={() => {
                                        setDraggedItem(null);
                                        setDragOverDate(null);
                                        setDragOverItemId(null);
                                      }}
                                      onDragOver={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (dragOverItemId !== item.afimaCampaignId) {
                                          setDragOverItemId(item.afimaCampaignId);
                                          setDragOverDate(dateStr);
                                        }
                                      }}
                                      onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (draggedItem && draggedItem.afimaCampaignId !== item.afimaCampaignId) {
                                          handleSwapItems(draggedItem, item);
                                        }
                                        setDraggedItem(null);
                                        setDragOverDate(null);
                                        setDragOverItemId(null);
                                      }}
                                      className={`p-1.5 rounded-md border text-xs shadow-2xs transition-all duration-150 hover:shadow-md cursor-pointer relative group flex items-center justify-between gap-1 ${espTheme.cardBg} ${espTheme.cardBorder} ${espTheme.cardHover} ${
                                        isSelected ? 'ring-2 ring-indigo-500 border-indigo-400' : ''
                                      } ${
                                        isItemDragged ? 'opacity-40 scale-95 ring-2 ring-dashed ring-indigo-400' : ''
                                      } ${
                                        isItemDragTarget ? 'ring-2 ring-indigo-500 scale-[1.02] shadow-lg border-indigo-400' : ''
                                      }`}
                                      onClick={() => { setSelectedCampaign(item); setIsManualCampaign(false); }}
                                    >
                                      <div className="flex items-center gap-1 min-w-0">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            toggleSelectCampaign({
                                              ...item,
                                              key: itemKey
                                            }, itemKey);
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          className="w-3 h-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer flex-shrink-0"
                                        />
                                        <span className="font-bold text-[10px] text-gray-700 font-mono flex-shrink-0">
                                          {formatCopenhagenTime(item.slotTime, item.scheduledDate)}
                                        </span>
                                        <span className="font-semibold text-gray-900 truncate text-[11px]" title={item.campaignName}>
                                          {item.campaignName}
                                        </span>
                                      </div>

                                      <div className="flex items-center gap-1 flex-shrink-0">
                                        {espId.startsWith('all_') && (
                                          <span className={`px-1 py-0.2 rounded text-[9px] font-bold ${espTheme.tagBg} ${espTheme.tagText} border ${espTheme.tagBorder}`}>
                                            {espTheme.name}
                                          </span>
                                        )}
                                        {item.dispatchPayload?.genderSegment && item.dispatchPayload.genderSegment.length === 1 && item.dispatchPayload.genderSegment[0] === 'F' && (
                                          <span className="px-1 py-0.2 rounded text-[9px] font-bold bg-pink-100 text-pink-700">♀</span>
                                        )}
                                        {item.includeNL && (
                                          <span className="px-1 py-0.2 rounded text-[9px] font-bold bg-purple-100 text-purple-700">+NL</span>
                                        )}
                                        <span className={`px-1 py-0.2 rounded text-[9px] font-bold text-white shadow-2xs ${getScoreColor(item.score)}`}>
                                          {item.score}
                                        </span>
                                        {item.status === 'pending' && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleAction('approve', { afimaCampaignId: item.afimaCampaignId });
                                            }}
                                            className="opacity-0 group-hover:opacity-100 p-0.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded transition-all flex-shrink-0 cursor-pointer shadow-2xs border border-emerald-200"
                                            title="Approve Slot"
                                          >
                                            <Check className="w-2.5 h-2.5" />
                                          </button>
                                        )}
                                        {(item.status === 'pending' || item.status === 'approved') && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSwappingItem(item);
                                            }}
                                            className="opacity-0 group-hover:opacity-100 p-0.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded transition-all flex-shrink-0 cursor-pointer shadow-2xs border border-indigo-200"
                                            title="Swap or Replace Campaign"
                                          >
                                            <ArrowLeftRight className="w-2.5 h-2.5" />
                                          </button>
                                        )}
                                        {item.validation && !item.validation.isValid ? (
                                          <AlertTriangle className="w-3 h-3 text-red-600 flex-shrink-0" />
                                        ) : isItemSent ? (
                                          <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-2xs flex-shrink-0" title="Status: Sent" />
                                        ) : isItemScheduled ? (
                                          <span className="w-2 h-2 rounded-xs bg-sky-500 shadow-2xs flex-shrink-0" title="Status: Pre-Scheduled on ESP" />
                                        ) : isItemApproved ? (
                                          <span className="w-2 h-2 rounded-full bg-teal-500 ring-2 ring-teal-200 shadow-2xs flex-shrink-0" title="Status: Approved (Ready for Push)" />
                                        ) : isItemPending ? (
                                          <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shadow-2xs flex-shrink-0" title="Status: Pending AI Suggestion" />
                                        ) : (
                                          <span className="w-2 h-2 rounded-full bg-red-500 shadow-2xs flex-shrink-0" title={`Status: ${item.status}`} />
                                        )}
                                      </div>

                                      {/* Rich Hover Card on Compact View */}
                                      <div className="hidden group-hover:block absolute bottom-full left-0 mb-1.5 w-60 p-2.5 bg-gray-900 text-white text-xs rounded-lg shadow-2xl z-30 pointer-events-none border border-gray-700">
                                        <div className="font-bold text-white mb-1 flex items-center justify-between">
                                          <span className="truncate pr-1">{item.campaignName}</span>
                                          <span className="text-[10px] text-indigo-300 font-normal uppercase">{item.status}</span>
                                        </div>
                                        <div className="text-gray-300 font-medium mb-1 line-clamp-2">
                                          {item.subjectLine}
                                        </div>
                                        {item.previewText && (
                                          <div className="text-gray-400 text-[10px] italic line-clamp-1 mb-1">
                                            {item.previewText}
                                          </div>
                                        )}
                                        <div className="text-gray-400 text-[10px] border-t border-gray-700 pt-1">
                                          {item.reasoning}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }

                                return (
                                  <div 
                                    key={item.afimaCampaignId} 
                                    draggable={item.status === 'pending' || item.status === 'approved'}
                                    onDragStart={(e) => {
                                      e.dataTransfer.setData('text/plain', item.afimaCampaignId);
                                      setDraggedItem(item);
                                    }}
                                    onDragEnd={() => {
                                      setDraggedItem(null);
                                      setDragOverDate(null);
                                      setDragOverItemId(null);
                                    }}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (dragOverItemId !== item.afimaCampaignId) {
                                        setDragOverItemId(item.afimaCampaignId);
                                        setDragOverDate(dateStr);
                                      }
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (draggedItem && draggedItem.afimaCampaignId !== item.afimaCampaignId) {
                                        handleSwapItems(draggedItem, item);
                                      }
                                      setDraggedItem(null);
                                      setDragOverDate(null);
                                      setDragOverItemId(null);
                                    }}
                                    className={`p-3 rounded-lg border text-sm shadow-sm transition-all duration-200 hover:shadow-md cursor-pointer relative ${espTheme.cardBg} ${espTheme.cardBorder} ${espTheme.cardHover} ${
                                      isSelected ? 'ring-2 ring-indigo-500 border-indigo-400 shadow-md' : ''
                                    } ${
                                      isItemDragged ? 'opacity-40 scale-95 ring-2 ring-dashed ring-indigo-400' : ''
                                    } ${
                                      isItemDragTarget ? 'ring-2 ring-indigo-500 scale-[1.02] shadow-lg border-indigo-400' : ''
                                    }`}
                                    onClick={() => { setSelectedCampaign(item); setIsManualCampaign(false); }}
                                  >
                                    <div className="flex justify-between items-start mb-2">
                                      <div className="flex items-center gap-1.5 min-w-0 pr-2">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            toggleSelectCampaign({
                                              ...item,
                                              key: itemKey
                                            }, itemKey);
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer flex-shrink-0"
                                          title="Select for batch unschedule"
                                        />
                                        <div title="Drag to move or swap" className="flex items-center cursor-grab active:cursor-grabbing" onClick={e => e.stopPropagation()}>
                                          <GripVertical 
                                            className="w-3.5 h-3.5 text-gray-400 hover:text-indigo-600 flex-shrink-0" 
                                          />
                                        </div>
                                        <span className="font-semibold text-gray-900 truncate text-xs" title={item.campaignName}>
                                          {item.campaignName}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-1.5 flex-shrink-0">
                                        {item.validation && !item.validation.isValid ? (
                                          <div 
                                            onClick={(e) => { 
                                              e.stopPropagation(); 
                                              setAuditReport({ items: [item.validation], issueCount: item.validation?.issues?.length || 0, allValid: false }); 
                                              setAuditModalOpen(true); 
                                            }}
                                            className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-100 text-red-700 border border-red-200 flex items-center gap-0.5 cursor-pointer hover:bg-red-200 shadow-sm"
                                            title={item.validation?.issues?.join(' | ') || 'Validation error'}
                                          >
                                            <AlertTriangle className="w-2.5 h-2.5 text-red-600" /> Defect
                                          </div>
                                        ) : isItemSent ? (
                                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-2xs" title="Status: Sent" />
                                        ) : isItemScheduled ? (
                                          <span className="w-2.5 h-2.5 rounded-xs bg-sky-500 shadow-2xs" title="Status: Pre-Scheduled on ESP" />
                                        ) : isItemApproved ? (
                                          <span className="w-2.5 h-2.5 rounded-full bg-teal-500 ring-2 ring-teal-200 shadow-2xs" title="Status: Approved (Ready for Push)" />
                                        ) : isItemPending ? (
                                          <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse shadow-2xs" title="Status: Pending AI Suggestion" />
                                        ) : (
                                          <span className="w-2.5 h-2.5 rounded-full bg-red-500 shadow-2xs" title={`Status: ${item.status}`} />
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-xs font-bold text-gray-800 flex items-center gap-1">
                                          <Clock className="w-3.5 h-3.5 text-indigo-600" />
                                          <span>{formatCopenhagenTime(item.slotTime, item.scheduledDate)}</span>
                                          <span className="text-[10px] text-gray-400 font-normal">CEST</span>
                                        </span>
                                        {espId.startsWith('all_') && (
                                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${espTheme.tagBg} ${espTheme.tagText} border ${espTheme.tagBorder}`}>
                                            {espTheme.name}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1">
                                        {item.dispatchPayload?.genderSegment && item.dispatchPayload.genderSegment.length === 1 && item.dispatchPayload.genderSegment[0] === 'F' && (
                                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-pink-100 text-pink-700 border border-pink-200" title="Audience: Female (F) Only">
                                            ♀ F
                                          </span>
                                        )}
                                        {item.dispatchPayload?.genderSegment && (
                                          (item.dispatchPayload.genderSegment.length === 2 && item.dispatchPayload.genderSegment.includes('M') && item.dispatchPayload.genderSegment.includes('U')) ||
                                          (item.dispatchPayload.genderSegment.length === 1 && item.dispatchPayload.genderSegment[0] === 'M')
                                        ) && (
                                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 border border-blue-200" title="Audience: Male + Undefined (M+U)">
                                            ♂ M+U
                                          </span>
                                        )}
                                        {item.includeNL && (
                                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700 border border-purple-200">
                                            +NL
                                          </span>
                                        )}
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold text-white shadow-sm ${getScoreColor(item.score)}`} title="Campaign Score">
                                          {item.score}
                                        </span>
                                      </div>
                                    </div>
                                    
                                    <div className="text-xs text-gray-500 line-clamp-2 mb-2 group relative cursor-help">
                                      {item.subjectLine}
                                      <div className="hidden group-hover:block absolute bottom-full left-0 mb-1 w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-xl z-10 break-words">
                                        {item.reasoning}
                                      </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex gap-1 justify-end pt-2 border-t border-black/5 items-center">
                                      {/* Swap / Replace Button */}
                                      {(item.status === 'pending' || item.status === 'approved') && (
                                        <button 
                                          onClick={(e) => { 
                                            e.stopPropagation(); 
                                            setSwappingItem(item); 
                                          }} 
                                          className="p-1.5 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 hover:text-indigo-700 rounded transition-colors mr-auto cursor-pointer" 
                                          title="Swap with slot or replace campaign"
                                        >
                                          <ArrowLeftRight className="w-3.5 h-3.5" />
                                        </button>
                                      )}

                                      {item.status === 'pending' && (
                                        <>
                                          <button onClick={(e) => { e.stopPropagation(); handleAction('approve', { afimaCampaignId: item.afimaCampaignId }); }} className="p-1.5 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700 rounded transition-colors cursor-pointer" title="Approve">
                                            <Check className="w-4 h-4" />
                                          </button>
                                          <button onClick={(e) => {
                                            e.stopPropagation();
                                            const reason = prompt('Reject reason?');
                                            if (reason) handleAction('reject', { afimaCampaignId: item.afimaCampaignId, reason });
                                          }} className="p-1.5 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 rounded transition-colors cursor-pointer" title="Reject">
                                            <X className="w-4 h-4" />
                                          </button>
                                        </>
                                      )}
                                      {item.status === 'approved' && (
                                        <button onClick={(e) => {
                                          e.stopPropagation();
                                          const reason = prompt('Hold reason?');
                                          if (reason) handleAction('hold', { afimaCampaignId: item.afimaCampaignId, reason });
                                        }} className="p-1.5 bg-orange-50 text-orange-600 hover:bg-orange-100 hover:text-orange-700 rounded transition-colors cursor-pointer" title="Hold">
                                          <Pause className="w-4 h-4" />
                                        </button>
                                      )}
                                      {(item.systemId === 'dp_se' || item.systemId?.startsWith('dp_')) && (item.status === 'pending' || item.status === 'approved') && (
                                        <button
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            try {
                                              const res = await fetch(`${API_BASE_URL}/api/auto-schedule/confirm-manual-schedule`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ afimaCampaignId: item.afimaCampaignId }),
                                              });
                                              if (res.ok) {
                                                fetchWeekPlan();
                                              }
                                            } catch (err) {
                                              console.error(err);
                                            }
                                          }}
                                          className="px-2 py-1 bg-pink-100 hover:bg-pink-200 text-pink-800 text-[10px] font-bold rounded flex items-center gap-1 transition-colors cursor-pointer border border-pink-300"
                                          title="Mark as Scheduled in Drip"
                                        >
                                          <Check className="w-3 h-3 text-pink-600" />
                                          Confirm in Drip
                                        </button>
                                      )}
                                      {isItemSent ? (
                                        <div className="flex items-center justify-between w-full text-[11px] font-semibold text-emerald-800">
                                          <span className="flex items-center gap-1">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Sent
                                          </span>
                                          {item.espCampaignId && (
                                            <span className="text-[10px] bg-emerald-100/90 px-1.5 py-0.5 rounded text-emerald-700 font-mono border border-emerald-200">
                                              #{item.espCampaignId}
                                            </span>
                                          )}
                                        </div>
                                      ) : item.status === 'dispatched' ? (
                                        <div className="flex items-center justify-between w-full text-[11px] font-semibold text-sky-800">
                                          <span className="flex items-center gap-1">
                                            {item.systemId === 'dp_se' || item.systemId?.startsWith('dp_') ? (
                                              <>
                                                <CheckCircle2 className="w-3.5 h-3.5 text-pink-600" />
                                                <span className="text-pink-800 font-bold">Scheduled in Drip</span>
                                              </>
                                            ) : (
                                              <>
                                                <Clock className="w-3.5 h-3.5 text-sky-600" /> Pre-Scheduled
                                              </>
                                            )}
                                          </span>
                                          {item.espCampaignId && (
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono border ${
                                              item.systemId === 'dp_se' || item.systemId?.startsWith('dp_')
                                                ? 'bg-pink-100/90 text-pink-700 border-pink-200'
                                                : 'bg-sky-100/90 text-sky-700 border-sky-200'
                                            }`}>
                                              #{item.espCampaignId}
                                            </span>
                                          )}
                                        </div>
                                      ) : null}
                                      {(item.status === 'held' || item.status === 'rejected') && (
                                        <div className="text-[10px] text-gray-500 italic truncate" title={item.holdReason || item.rejectReason}>
                                          {item.holdReason || item.rejectReason}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              }) : (
                                scheduledForDay.length === 0 && (
                                  <div 
                                    onClick={() => { setManualAddDate(dateStr); setManualAddOpen(true); }}
                                    className={`${
                                      viewMode === '7d' ? 'h-24' : 'h-16'
                                    } rounded-lg border-2 border-dashed border-gray-200 hover:border-indigo-400 hover:bg-indigo-50/50 cursor-pointer flex flex-col items-center justify-center text-gray-400 hover:text-indigo-600 text-[11px] font-medium transition-all group p-1`}
                                    title={`Click to manually add a campaign to ${day}`}
                                  >
                                    <Plus className="w-3.5 h-3.5 mb-0.5 text-gray-300 group-hover:text-indigo-500 transition-colors" />
                                    <span>+ Add</span>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center text-gray-500">Select a date range to view the plan.</div>
            )}
          </CardContent>
        </Card>

        {/* Action Bar */}
        <div className="bg-white rounded-xl p-4 shadow-sm border flex items-center justify-between">
          <div className="text-sm text-gray-600 font-medium">
            {weekPlan?.summary?.total || 0} total items • {weekPlan?.summary?.pending || 0} pending action
          </div>
          <div className="flex gap-3">
            <button 
              onClick={handleClearPlan}
              disabled={!weekPlan?.summary?.total}
              className="px-4 py-2 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              Clear Plan
            </button>
            <button 
              onClick={handleRegenerate}
              className="px-4 py-2 text-sm font-semibold text-orange-700 bg-orange-50 border border-orange-200 hover:bg-orange-100 rounded-lg transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Regenerate Week
            </button>
            <button 
              onClick={async () => {
                setLoading(true);
                try {
                  const res = await fetch(`${API_BASE_URL}/api/auto-schedule/validate-plan`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ espId, weekId }),
                  });
                  if (res.ok) {
                    const valData = await res.json();
                    setAuditReport(valData);
                    setAuditModalOpen(true);
                  }
                } catch (e: any) {
                  alert(`Audit error: ${e.message}`);
                } finally {
                  setLoading(false);
                }
              }}
              disabled={!weekPlan?.summary?.total}
              className="px-4 py-2 text-sm font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 rounded-lg transition-colors flex items-center gap-2 shadow-sm disabled:opacity-50"
            >
              <ShieldCheck className="w-4 h-4 text-indigo-600" />
              Safety Audit
            </button>
            <button 
              onClick={handleApproveAll}
              disabled={!weekPlan?.summary?.pending}
              className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors flex items-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="w-4 h-4" />
              Approve All Pending
            </button>
            <button 
              onClick={handleScheduleAllNow}
              disabled={!weekPlan?.items?.some(i => i.status === 'approved' || i.status === 'pending')}
              title={planStats.approved ? `Push ${planStats.approved} approved campaign(s) to ${ESP_LABELS[espId] || 'ESP'}` : 'Push campaigns to ESP'}
              className="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center gap-2 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              🚀 Push {planStats.approved ? `${planStats.approved} Approved` : 'All'} to {ESP_LABELS[espId] || 'ESP'} Now
            </button>
          </div>
        </div>

        {/* Floating Batch Selection Bar */}
        {selectedCampaigns.size > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900/95 text-white px-5 py-3 rounded-2xl shadow-2xl border border-slate-700 backdrop-blur-md flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-200">
            <div className="flex items-center gap-2">
              <span className="flex h-2.5 w-2.5 rounded-full bg-indigo-400 animate-pulse" />
              <span className="text-sm font-semibold text-slate-100">
                {selectedCampaigns.size} campaign{selectedCampaigns.size > 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="h-4 w-px bg-slate-700" />
            <div className="flex items-center gap-2">
              <button
                onClick={handleMultiUnschedule}
                disabled={loading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                Unschedule / Delete Selected ({selectedCampaigns.size})
              </button>
              <button
                onClick={() => setSelectedCampaigns(new Map())}
                className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg text-xs font-medium transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Today's Dispatches */}
        <Card className="shadow-sm">
          <CardHeader className="bg-gray-50/50 border-b pb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-800">
              <Play className="w-5 h-5 text-blue-600" />
              Today's Dispatches
            </h2>
          </CardHeader>
          <CardContent className="p-0">
            {todayDispatches.some(d => d.dryRun) && (
              <div className="bg-yellow-50 text-yellow-800 px-4 py-2 border-b border-yellow-200 text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Dispatches contain dry-run campaigns. They will not be sent to ESP.
              </div>
            )}
            <div className="divide-y">
              {todayDispatches.length > 0 ? todayDispatches.map(item => (
                <div key={item.afimaCampaignId} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                  <div>
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      {item.campaignName}
                      {item.dryRun && <span className="text-[10px] bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded font-bold uppercase">Dry Run</span>}
                    </h4>
                    <p className="text-sm text-gray-500">{item.subjectLine}</p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColors(item.status)}`}>
                      {item.status.toUpperCase()}
                    </span>
                    <p className="text-xs text-gray-500 mt-1 font-mono">
                      {item.dispatchedAt ? new Date(item.dispatchedAt).toLocaleTimeString() : 'Pending dispatch'}
                    </p>
                  </div>
                </div>
              )) : (
                <div className="p-6 text-center text-gray-500 text-sm">No dispatches scheduled for today.</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* History */}
        <Card className="shadow-sm overflow-hidden">
          <div 
            className="bg-gray-50/50 p-4 cursor-pointer flex items-center justify-between hover:bg-gray-100 transition-colors"
            onClick={() => setHistoryOpen(!historyOpen)}
          >
            <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-800">
              <History className="w-5 h-5 text-gray-600" />
              History (Last 4 Weeks)
            </h2>
            <span className="text-sm text-indigo-600 font-medium">{historyOpen ? 'Hide' : 'Show'}</span>
          </div>
          {historyOpen && (
            <div className="border-t">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase font-semibold">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Time</th>
                    <th className="px-4 py-3">ESP</th>
                    <th className="px-4 py-3">Campaign</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Score</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {history.length > 0 ? history.map((item: any, idx: number) => {
                    const displayDate = item.scheduledDate ? (item.scheduledDate.includes('T') ? item.scheduledDate.split('T')[0] : item.scheduledDate.slice(0, 10)) : '-';
                    const displayTime = item.slotTime || (item.scheduledDate && item.scheduledDate.includes('T') ? item.scheduledDate.split('T')[1].slice(0, 5) : '10:00');
                    const espBadge = (item.systemId || item.espId || espId || '').toUpperCase();

                    return (
                      <tr key={item.afimaCampaignId || item.scheduledId || item.id || `hist-${idx}-${item.campaignName}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-700">{displayDate}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-500">{displayTime}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                            {espBadge}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">{item.campaignName || item.name}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${getStatusColors(item.status)}`}>
                            {item.status || 'sent'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-white text-[10px] font-bold ${getScoreColor(item.score || 0)}`}>
                            {item.score || 0}
                          </span>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        {historyOpen && history.length === 0 ? 'No history available.' : 'Loading history...'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        </>
        )}

        {selectedCampaign && (
          <CampaignDetailModal 
            campaign={selectedCampaign} 
            isManual={isManualCampaign} 
            onClose={() => setSelectedCampaign(null)} 
            onAction={handleAction} 
            onUnschedule={handleUnscheduleCampaign}
            onOpenSwap={(c) => {
              setSelectedCampaign(null);
              setSwappingItem(c);
            }}
            onSaveSuccess={async () => {
              await fetchWeekPlan();
            }}
          />
        )}

        {swappingItem && (
          <SwapCampaignModal
            item={swappingItem}
            weekPlan={weekPlan}
            espId={espId}
            onClose={() => setSwappingItem(null)}
            onSwapSlots={handleSwapItems}
            onReplaceCampaign={handleReplaceSlotCampaign}
          />
        )}

        {auditModalOpen && auditReport && (
          <AuditFailsafeModal
            auditReport={auditReport}
            isRepairing={isRepairing}
            onClose={() => setAuditModalOpen(false)}
            onAutoRepairAll={handleAutoRepairAll}
            onAutoRepairSingle={handleAutoRepairSingle}
            onApproveValidOnly={async () => {
              await handleAction('approve-week', { espId, weekId, startDate: activeBaseDate, days: activeDays });
              setAuditModalOpen(false);
            }}
          />
        )}

        <ManualAddCampaignModal
          isOpen={manualAddOpen}
          initialDate={manualAddDate}
          espId={espId}
          weekId={weekId}
          onClose={() => setManualAddOpen(false)}
          onSuccess={async (msg) => {
            setGenerateResult(msg);
            await Promise.all([fetchWeekPlan(), fetchAlreadyScheduled(), fetchToday()]);
          }}
        />

        <GeneratePlanModal
          isOpen={generateModalOpen}
          espId={espId}
          defaultStartDate={activeBaseDate}
          defaultDays={7}
          initialPrompt={userPrompt}
          onClose={() => setGenerateModalOpen(false)}
          onGenerate={(params) => {
            handleGenerate(params);
          }}
          onSuccess={(msg) => setGenerateResult(msg)}
          onRefresh={async () => {
            await Promise.all([fetchWeekPlan(), fetchAlreadyScheduled(), fetchToday()]);
          }}
        />

        <OperationProgressModal
          state={progressModal}
          onClose={() => setProgressModal(prev => ({ ...prev, open: false }))}
        />

      </div>
    </MainLayout>
  );
}

interface GeneratePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  espId: string;
  defaultStartDate: string;
  defaultDays: number;
  initialPrompt: string;
  onGenerate: (params: { startDate: string; days: number; prompt: string }) => void;
  onSuccess: (resultMsg: string) => void;
  onRefresh: () => Promise<void>;
}

function GeneratePlanModal({
  isOpen,
  onClose,
  espId,
  defaultStartDate,
  defaultDays,
  initialPrompt,
  onGenerate,
  onSuccess,
  onRefresh,
}: GeneratePlanModalProps) {
  const todayStr = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  })();

  // Compute current cycle Tuesday and Monday
  const currentTuesday = (() => {
    const d = new Date();
    const day = d.getDay() === 0 ? 7 : d.getDay();
    const diffToTue = day === 1 ? -6 : 2 - day;
    const tue = new Date(d);
    tue.setDate(d.getDate() + diffToTue);
    const y = tue.getFullYear();
    const m = String(tue.getMonth() + 1).padStart(2, '0');
    const dNum = String(tue.getDate()).padStart(2, '0');
    return `${y}-${m}-${dNum}`;
  })();
  const currentMonday = addDays(currentTuesday, 6);

  const initialStart = todayStr;
  const initialEnd = initialStart <= currentMonday ? currentMonday : addDays(initialStart, 6);
  const initialCalcDays = calcDaysBetween(initialStart, initialEnd);

  const [startDate, setStartDate] = useState<string>(initialStart);
  const [endDate, setEndDate] = useState<string>(initialEnd);
  const [days, setDays] = useState<number>(initialCalcDays);
  const [prompt, setPrompt] = useState<string>(initialPrompt || '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      const s = todayStr;
      const e = s <= currentMonday ? currentMonday : addDays(s, 6);
      setStartDate(s);
      setEndDate(e);
      setDays(calcDaysBetween(s, e));
      setPrompt(initialPrompt || '');
      setError(null);
    }
  }, [isOpen, todayStr, currentMonday, initialPrompt]);

  if (!isOpen) return null;

  const handleStartChange = (val: string) => {
    setStartDate(val);
    if (!endDate || val > endDate) {
      setEndDate(val);
      setDays(1);
    } else {
      setDays(calcDaysBetween(val, endDate));
    }
  };

  const handleEndChange = (val: string) => {
    setEndDate(val);
    if (!startDate || val < startDate) {
      setStartDate(val);
      setDays(1);
    } else {
      setDays(calcDaysBetween(startDate, val));
    }
  };

  const handlePresetDays = (numDays: number) => {
    const newEnd = addDays(startDate, numDays - 1);
    setEndDate(newEnd);
    setDays(numDays);
  };

  const handlePresetToMonday = () => {
    setStartDate(todayStr);
    setEndDate(currentMonday);
    setDays(calcDaysBetween(todayStr, currentMonday));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onClose();
    onGenerate({
      startDate,
      days,
      prompt,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-4">
        <div className="flex items-center justify-between border-b pb-3">
          <div className="flex items-center gap-2 text-indigo-700 font-bold text-lg">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            <span>Generate Plan</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-xs font-medium border border-red-200">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Plan Duration Quick Presets */}
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase mb-1.5">
              Plan Duration Presets
            </label>
            <div className="grid grid-cols-4 gap-1.5">
              <button
                type="button"
                onClick={handlePresetToMonday}
                className={`py-2 px-1.5 rounded-lg border text-xs font-bold transition-all flex flex-col items-center gap-0.5 cursor-pointer ${
                  endDate === currentMonday && startDate === todayStr
                    ? 'bg-indigo-50 border-indigo-600 text-indigo-700 ring-2 ring-indigo-200'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span>🎯 To Mon</span>
                <span className="text-[10px] font-normal text-gray-500">{calcDaysBetween(todayStr, currentMonday)} Days</span>
              </button>

              <button
                type="button"
                onClick={() => handlePresetDays(7)}
                className={`py-2 px-1.5 rounded-lg border text-xs font-bold transition-all flex flex-col items-center gap-0.5 cursor-pointer ${
                  days === 7 && (endDate !== currentMonday || startDate !== todayStr)
                    ? 'bg-indigo-50 border-indigo-600 text-indigo-700 ring-2 ring-indigo-200'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span>📅 1 Week</span>
                <span className="text-[10px] font-normal text-gray-500">7 Days</span>
              </button>

              <button
                type="button"
                onClick={() => handlePresetDays(14)}
                className={`py-2 px-1.5 rounded-lg border text-xs font-bold transition-all flex flex-col items-center gap-0.5 cursor-pointer ${
                  days === 14
                    ? 'bg-indigo-50 border-indigo-600 text-indigo-700 ring-2 ring-indigo-200'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span>🚀 2 Weeks</span>
                <span className="text-[10px] font-normal text-gray-500">14 Days</span>
              </button>

              <button
                type="button"
                onClick={() => handlePresetDays(28)}
                className={`py-2 px-1.5 rounded-lg border text-xs font-bold transition-all flex flex-col items-center gap-0.5 cursor-pointer ${
                  days === 28
                    ? 'bg-indigo-50 border-indigo-600 text-indigo-700 ring-2 ring-indigo-200'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span>🗓️ Month</span>
                <span className="text-[10px] font-normal text-gray-500">28 Days</span>
              </button>
            </div>
          </div>

          {/* Date Range Selection: Start Date & End Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => handleStartChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none cursor-pointer"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => handleEndChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none cursor-pointer"
                required
              />
            </div>
          </div>

          {/* Range summary indicator badge */}
          <div className="bg-indigo-50/70 border border-indigo-100 rounded-lg px-3 py-1.5 flex items-center justify-between text-xs text-indigo-800">
            <span className="font-semibold flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-indigo-600" />
              Duration: {days} Day{days > 1 ? 's' : ''}
            </span>
            <span className="text-[11px] text-indigo-600 font-medium">
              {formatDateLabel(startDate).label} – {formatDateLabel(endDate).label}
            </span>
          </div>

          {/* AI Instructions / Prompt */}
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
              AI Custom Instructions (Optional)
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. Focus on Loan Refinance on weekdays. Send Sambla twice with 4 days apart. Rotate between TA1 and TA2."
              rows={3}
              className="w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none placeholder-gray-400"
            />
          </div>

          {/* Buttons */}
          <div className="pt-3 border-t flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-all shadow-md flex items-center gap-1.5 cursor-pointer"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Generate {days}-Day Plan
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function cleanBrandName(rawName: string): string {
  if (!rawName) return '';
  let b = rawName.trim();
  if (b.toLowerCase().startsWith('lp-')) b = b.slice(3);
  if (b.includes(' - ')) b = b.split(' - ')[0].trim();
  if (b.includes(': ')) b = b.split(': ')[0].trim();
  b = b.replace(/\s+(TG|TA|TO|TB|TL|TM|T)\d+[a-z]?$/i, '').trim();
  b = b.replace(/\s+(NO|SE|FI|DE|DK|NO2|SE2|FI2|DE2|DK2|V2|V3)$/i, '').trim();
  b = b.replace(/-(no|se|fi|de|dk|v2|v3)$/i, '').trim();
  if (b.includes('-') && !/[A-Z]/.test(b)) {
    b = b.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
  }
  const specialMap: Record<string, string> = {
    'klikklan': 'Klikklån',
    'klikklån': 'Klikklån',
    'supermarket': 'Sparklubben',
    'ica': 'Sparklubben',
    'coop': 'Coop',
    'willys': 'Willys',
    'craveless': 'Craveless',
    'vitaepro': 'VitaePro',
    'sleepmore': 'Sleepmore',
    'justincase': 'JustInCase',
    'tryggsam': 'Tryggsam',
    'verisure': 'Verisure',
    'insurello': 'Insurello',
    'northmill': 'Northmill',
    'enkelfinans': 'Enkelfinans',
    'enklare': 'Enklare',
    'advisa': 'Advisa',
    'sambla': 'Sambla',
    'lendo': 'Lendo',
    'zmarta': 'Zmarta',
    'freedom finance': 'Freedom Finance',
    'freedom': 'Freedom Finance',
    'digifinans': 'Digifinans',
    'uno finans': 'Uno Finans',
    'unofinans': 'Uno Finans',
    'rahalaitos': 'Rahalaitos',
    'sortter': 'Sortter',
    'omalaina': 'Omalaina',
    'unolaina': 'Unolaina',
    'kattokorko': 'Kattokorko',
    'creditstar': 'Creditstar',
    'axolaina': 'Axolaina',
    'ferratum': 'Ferratum',
    'ferratum cc': 'Ferratum',
    'ferratumcc': 'Ferratum',
    'motty kredittkort': 'Motty Kredittkort',
    'motty forbrukslån': 'Motty Forbrukslån',
    'motty': 'Motty',
    'axofinans': 'Axo Finans',
    'axo': 'Axo Finans',
    'simplyloan': 'SimplyLoan',
  };
  const low = b.toLowerCase();
  if (specialMap[low]) return specialMap[low];
  return b;
}

function safeHtmlString(val: any): string {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    if (typeof val.html === 'string') return val.html;
    if (typeof val.content === 'string') return val.content;
    if (typeof val.htmlContent === 'string') return val.htmlContent;
    if (typeof val.body === 'string') return val.body;
  }
  return '';
}

const ESP_TRACKING_CONFIG: Record<string, { domain: string; pathPrefix: string; s1: string; s2Macro: string }> = {
  // Sweden
  feedblitz_se: { domain: 'klarkredit24.se', pathPrefix: '/go/', s1: 'fbse', s2Macro: '<$LeadID$>' },
  fb_se: { domain: 'klarkredit24.se', pathPrefix: '/go/', s1: 'fbse', s2Macro: '<$LeadID$>' },
  gr_se: { domain: 'sparkredit.se', pathPrefix: '/go/', s1: 'grse', s2Macro: '[[leadid]]' },
  gr_se2: { domain: 'svenskalaan.se', pathPrefix: '/go/', s1: 'grse2', s2Macro: '[[leadid]]' },
  ms_se: { domain: 'swiftkredit.se', pathPrefix: '/go/', s1: 'msse', s2Macro: '#recipient:LeadID#' },
  dp_se: { domain: 'enkeltlaan.com', pathPrefix: '/go/', s1: 'dpse', s2Macro: '{{ subscriber.LeadID }}' },
  drip_se: { domain: 'enkeltlaan.com', pathPrefix: '/go/', s1: 'dpse', s2Macro: '{{ subscriber.LeadID }}' },
  br_sl_se: { domain: 'simplyloan.se', pathPrefix: '/slgo/', s1: 'slseem', s2Macro: '{{ contact.CID }}' },
  
  // Norway
  feedblitz_no: { domain: 'provepakker.com', pathPrefix: '/go/', s1: 'fbno', s2Macro: '<$LeadID$>' },
  fb_no: { domain: 'provepakker.com', pathPrefix: '/go/', s1: 'fbno', s2Macro: '<$LeadID$>' },
  gr_no: { domain: 'laninorge.com', pathPrefix: '/go/', s1: 'grno', s2Macro: '[[leadid]]' },
  br_sl_no: { domain: 'simplyloan.no', pathPrefix: '/slgo/', s1: 'slnoem', s2Macro: '{{ contact.CID }}' },

  // Finland
  feedblitz_fi: { domain: 'parhaatedut.com', pathPrefix: '/go/', s1: 'fbfi', s2Macro: '<$LeadID$>' },
  fb_fi: { domain: 'parhaatedut.com', pathPrefix: '/go/', s1: 'fbfi', s2Macro: '<$LeadID$>' },
  gr_fi: { domain: 'suomalaisetlainat.com', pathPrefix: '/go/', s1: 'grfi', s2Macro: '[[leadid]]' },
  ml_fi: { domain: 'tarjouksesi.com', pathPrefix: '/go/', s1: 'mlfi', s2Macro: '{$leadid}' },
  br_fi: { domain: 'lainatsuomessa.com', pathPrefix: '/go/', s1: 'brfi', s2Macro: '{{contact.LEADID}}' },
  br_sl_fi: { domain: 'simplyloan.fi', pathPrefix: '/slgo/', s1: 'slfiem', s2Macro: '{{ contact.CID }}' },

  // Germany
  feedblitz_de: { domain: 'sparfuechse-aktuell.de', pathPrefix: '/go/', s1: 'fbde', s2Macro: '<$LeadID$>' },
  fb_de: { domain: 'sparfuechse-aktuell.de', pathPrefix: '/go/', s1: 'fbde', s2Macro: '<$LeadID$>' },
  gr_de: { domain: 'gewinnspielen.com', pathPrefix: '/go/', s1: 'grde', s2Macro: '[[leadid]]' },
};

function CampaignDetailModal({ 
  campaign, 
  isManual, 
  onClose, 
  onAction,
  onUnschedule,
  onSaveSuccess,
  onOpenSwap,
}: { 
  campaign: any, 
  isManual?: boolean, 
  onClose: () => void, 
  onAction: (endpoint: string, payload: any) => Promise<void>,
  onUnschedule?: (campaign: any) => Promise<void>,
  onSaveSuccess?: () => Promise<void> | void,
  onOpenSwap?: (campaign: any) => void,
}) {
  const [subject, setSubject] = useState(campaign.subjectLine || campaign.subject || '');
  const [preview, setPreview] = useState(() => {
    if (campaign.previewText && typeof campaign.previewText === 'string' && campaign.previewText.trim()) return campaign.previewText.trim();
    if (campaign.preheader && typeof campaign.preheader === 'string' && campaign.preheader.trim()) return campaign.preheader.trim();
    if (campaign.preview && typeof campaign.preview === 'string' && campaign.preview.trim()) return campaign.preview.trim();
    if (campaign.dispatchPayload?.previewText && typeof campaign.dispatchPayload.previewText === 'string' && campaign.dispatchPayload.previewText.trim()) return campaign.dispatchPayload.previewText.trim();
    const html = safeHtmlString(campaign.htmlContent || campaign.dispatchPayload?.htmlContent);
    const m = html.match(/<!--\s*Preheader\s*-->\s*<div[^>]*>(.*?)<\/div>/i);
    if (m && m[1] && m[1].trim()) return m[1].trim();
    return '';
  });
  const [brandName, setBrandName] = useState(() => {
    return cleanBrandName(campaign.brandName || campaign.dispatchPayload?.brandName || campaign.campaignName || campaign.name || campaign.advertiser || '');
  });
  const [time, setTime] = useState(campaign.slotTime || '');
  const [prompt, setPrompt] = useState('');
  const [alternatives, setAlternatives] = useState<any[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isImproving, setIsImproving] = useState(false);
  const [currentHtml, setCurrentHtml] = useState<string>(() => safeHtmlString(campaign.htmlContent || campaign.dispatchPayload?.htmlContent));
  const [htmlEditMode, setHtmlEditMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subjectPairs, setSubjectPairs] = useState<any[]>(campaign.subjectPairs || []);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [confirmingDrip, setConfirmingDrip] = useState(false);

  const handleCopyText = (text: string, fieldName: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleConfirmDrip = async () => {
    try {
      setConfirmingDrip(true);
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/confirm-manual-schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          afimaCampaignId: campaign.afimaCampaignId,
        }),
      });
      if (res.ok) {
        if (onSaveSuccess) await onSaveSuccess();
        onClose();
      } else {
        const err = await res.json();
        alert(`Failed to confirm Drip schedule: ${err.error || res.statusText}`);
      }
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    } finally {
      setConfirmingDrip(false);
    }
  };

  useEffect(() => {
    setCurrentHtml(safeHtmlString(campaign.htmlContent || campaign.dispatchPayload?.htmlContent));
    setSubject(campaign.subjectLine || campaign.dispatchPayload?.subject || '');
    setPreview(campaign.previewText || campaign.dispatchPayload?.previewText || '');
    setBrandName(campaign.brandName || campaign.dispatchPayload?.brandName || '');
  }, [campaign]);

  useEffect(() => {
    const fetchPairs = async () => {
      try {
        const rawFullName = (campaign.campaignName || campaign.name || '').trim();
        // Robust regex to extract advertiser and edition (e.g. 'Rahalaitos TL5E' -> adv: 'Rahalaitos', ed: 'TL5E')
        const edMatch = rawFullName.match(/^(.*?)(?:\s+([Tt][A-Za-z]?\d+[a-zA-Z]?|\b[Tt]\d+\b))\s*$/);
        const parsedAdv = edMatch ? edMatch[1].trim() : rawFullName;
        const parsedEd = edMatch ? edMatch[2].trim() : (campaign.textEdition || 'TA1');

        const rawAdv = parsedAdv.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-(se|no|fi|de)$/, '').replace(/^-|-$/g, '');
        const cleanAdv = parsedAdv.toLowerCase().replace(/[^a-z0-9]/g, '');
        const sysId = (campaign.systemId || campaign.espId || '').toLowerCase();
        const geoSuffix = sysId.includes('_no') ? '-no' : sysId.includes('_fi') ? '-fi' : sysId.includes('_de') ? '-de' : '-se';

        const candIds = Array.from(new Set([
          campaign.campaignId,
          `${rawAdv}${geoSuffix}`,
          rawAdv,
          parsedAdv.toLowerCase().replace(/ /g, '-'),
          `${cleanAdv}${geoSuffix}`,
          cleanAdv,
          rawAdv.replace(/-finans$/, '') + geoSuffix,
          rawAdv.replace(/-finans$/, ''),
          rawAdv.replace(/-kredittkort$/, '') + '-kredittkort' + geoSuffix,
          rawAdv.replace(/-kredittkort$/, ''),
        ])).filter(Boolean);

        for (const cid of candIds) {
          const res = await fetch(`${API_BASE_URL}/api/text-library/versions?campaignId=${cid}`);
          if (res.ok) {
            const data = await res.json();
            const edUpper = (parsedEd || campaign.textEdition || 'TA1').toUpperCase();
            const edNum = edUpper.replace(/[^0-9]/g, '') || '1';
            const edSuffix = edUpper.replace(/[0-9]/g, '').slice(-1).toLowerCase();

            const match = data.versions?.find((v: any) => {
              const vId = (v.versionId || '').toUpperCase();
              if (vId === edUpper) return true;
              if (vId.replace(/[^0-9]/g, '') === edNum && (vId.toLowerCase().endsWith(edSuffix) || !edSuffix)) return true;
              return false;
            }) || data.versions?.[0];

            if (match) {
              // Always load subject pairs for the dropdown selector
              const pairs = (match.subjectPairs && match.subjectPairs.length > 0)
                ? match.subjectPairs
                : (match.subjectLines && match.subjectLines.length > 0)
                  ? match.subjectLines.map((s: string) => ({ subject: s, previewText: match.previewText || '' }))
                  : [];
              if (pairs.length > 0) {
                setSubjectPairs(pairs);
              }

              // Only fill in EMPTY fields — never overwrite existing saved data.
              // The stored HTML/subject/preview in AfimaGlobalCampaigns is the source of truth.
              // Use "Repair from Library" button for explicit re-render from TextVersions.
              const existingHtml = campaign.htmlContent || campaign.dispatchPayload?.htmlContent || '';
              const existingSubject = campaign.subjectLine || campaign.dispatchPayload?.subject || '';
              const existingPreview = campaign.previewText || campaign.dispatchPayload?.previewText || '';
              const hasValidHtml = existingHtml.length > 400;

              if (!hasValidHtml) {
                let rawHtml = match.htmlContent;
                if (!rawHtml && match.textContent) {
                  try {
                    const rRes = await fetch(`${API_BASE_URL}/api/text-library/render-text`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        textContent: match.textContent,
                        previewText: match.previewText || '',
                        disclaimer: match.disclaimer || '',
                        espId: sysId,
                        geo: sysId.split('_')[1]?.toUpperCase() || 'SE',
                        advertiserName: match.name || cleanBrandName(parsedAdv),
                        campaignId: cid,
                        versionId: match.versionId || edUpper,
                      }),
                    });
                    if (rRes.ok) {
                      const rData = await rRes.json();
                      if (rData.html) {
                        rawHtml = rData.html;
                      }
                    }
                  } catch (e) {
                    console.warn('Auto-render fallback failed:', e);
                  }
                }

                if (rawHtml) {
                  let clHtml = rawHtml.replace('{afimaCampaignId}', campaign.afimaCampaignId || '');
                  clHtml = clHtml.replace(/av\s+Klikklån\s+NO/gi, 'av Klikklån')
                                 .replace(/til\s+Klikklån\s+NO/gi, 'til Klikklån')
                                 .replace(/(av|til|von)\s+([A-Za-zÆØÅæøå]+)\s+(NO|SE|FI|DE)/gi, '$1 $2');
                  if (isGR) {
                    clHtml = clHtml.replace(/\{\{CUSTOM\s*'?leadid'?\}\}/gi, '[[leadid]]')
                                   .replace(/\{\{leadid\}\}/gi, '[[leadid]]');
                  } else if (sysId === 'ms_se') {
                    clHtml = clHtml.replace(/\{\{CUSTOM\s*'?leadid'?\}\}/gi, '#recipient:LeadID#')
                                   .replace(/\{\{leadid\}\}/gi, '#recipient:LeadID#')
                                   .replace(/#lead_id#/g, '#recipient:LeadID#');
                  } else if (sysId === 'dp_se' || sysId.startsWith('dp_') || sysId.includes('drip')) {
                    clHtml = clHtml.replace(/\{\{CUSTOM\s*'?leadid'?\}\}/gi, '{{ subscriber.LeadID }}')
                                   .replace(/\{\{leadid\}\}/gi, '{{ subscriber.LeadID }}')
                                   .replace(/#lead_id#/g, '{{ subscriber.LeadID }}')
                                   .replace(/\{\{\s*subscriber\.first_name\s*\}\}/g, '{{ subscriber.fname }}')
                                   .replace(/\{\{\s*subscriber\.leadid\s*\}\}/g, '{{ subscriber.LeadID }}');
                    // Ensure Drip compliance footer tags are present
                    if (!clHtml.includes('{{ unsubscribe_link }}') && !clHtml.includes('{{unsubscribe_link}}')) {
                      clHtml = clHtml.replace(/<p[^>]*>.*?Afima ApS &bull;.*?<\/p>\s*<p[^>]*>.*?Avregistrera dig h&auml;r.*?<\/p>/gis, '<p style="text-align: center; margin: 0 0 4px 0;"><span style="color: #808080; font-size: 12px; line-height: 1.5;">{{ unsubscribe_link }}<br />{{ inline_postal_address }}</span></p>');
                    }
                  } else if (sysId === 'ml_fi' || sysId.startsWith('ml_')) {
                    clHtml = clHtml.replace(/\{\{CUSTOM\s*'?leadid'?\}\}/gi, '{$leadid}')
                                   .replace(/\{\{leadid\}\}/gi, '{$leadid}');
                  } else {
                    clHtml = clHtml.replace(/\{\{CUSTOM\s*'?leadid'?\}\}/gi, '<$LeadID$>')
                                   .replace(/\{\{leadid\}\}/gi, '<$LeadID$>');
                  }
                  setCurrentHtml(clHtml);
                }
              }
              if (!existingSubject && match.subject) {
                setSubject(match.subject);
              }
              if (!existingPreview && match.previewText) {
                setPreview(match.previewText);
              }
              if (!campaign.brandName && (match.brandName || parsedAdv)) {
                setBrandName(match.brandName || cleanBrandName(parsedAdv));
              }
              break;
            }
          }
        }
      } catch (e) {
        console.warn('Failed to load subject pairs for modal:', e);
      }
    };
    fetchPairs();
  }, [campaign]);

  // Audience & Segmentation State
  const sysId = (campaign.systemId || campaign.espId || '').toLowerCase();
  const isDrip = sysId === 'dp_se' || sysId.startsWith('dp_') || sysId.includes('drip');
  const isGR = sysId.startsWith('gr_') || sysId.includes('getresponse');
  const isGRSparkredit = sysId === 'gr_se';

  const defaultList = campaign.dispatchPayload?.listId || (
    sysId === 'gr_se' ? 'ixQq9' :
    sysId === 'gr_se2' ? 'Pr1vv' :
    sysId === 'gr_no' ? 'Xk5x1' :
    sysId === 'gr_fi' ? 'Zd2hu' :
    sysId === 'gr_de' ? 'jjrg7' :
    sysId === 'ml_fi' ? '181013946874987943' :
    isDrip ? 'daily_newsletter' :
    sysId.includes('_no') ? '1128058' :
    sysId.includes('_fi') ? '1121236' :
    sysId.includes('_de') ? '1126000' : '1110202'
  );
  const [listId, setListId] = useState(defaultList);
  const [genderSegment, setGenderSegment] = useState<string[]>(
    campaign.dispatchPayload?.genderSegment && campaign.dispatchPayload.genderSegment.length > 0
      ? campaign.dispatchPayload.genderSegment
      : ['M', 'F', 'U']
  );
  const defaultInc = campaign.dispatchPayload?.includedSegments ?? (
    isGRSparkredit ? [] :
    sysId === 'gr_se2' ? ['8OvC0'] :
    sysId === 'gr_no' ? ['TEAH1'] :
    sysId === 'gr_fi' ? ['KQuI6'] :
    sysId === 'gr_de' ? ['4PWe7'] : []
  );
  const [includedSegments, setIncludedSegments] = useState<string[]>(defaultInc);
  const [excludedSegments, setExcludedSegments] = useState<string[]>(
    isGR ? [] : (campaign.dispatchPayload?.excludedSegments || [])
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleSaveSettings = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const cleanTime = (time || '').replace('.', ':').trim();
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/update-dispatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          afimaCampaignId: campaign.afimaCampaignId,
          subjectLine: subject,
          previewText: preview,
          brandName,
          slotTime: cleanTime,
          htmlContent: currentHtml,
          listId,
          genderSegment,
          includedSegments,
          excludedSegments,
        })
      });
      if (res.ok) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        if (onSaveSuccess) {
          await onSaveSuccess();
        }
      } else {
        const d = await res.json();
        throw new Error(d.error || 'Failed to save');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const isSubjectModified = subject !== (campaign.subjectLine || campaign.subject || '');
  const isPreviewModified = preview !== (campaign.previewText || '');

  const rawFullName = (campaign.campaignName || campaign.name || '').trim();
  const edMatch = rawFullName.match(/^(.*?)(?:\s+([Tt][A-Za-z]?\d+[a-zA-Z]?|\b[Tt]\d+\b))\s*$/);
  const advertiser = edMatch ? edMatch[1].trim() : rawFullName;

  const detectedGeo = useMemo(() => {
    const sys = (campaign.systemId || campaign.espId || '').toLowerCase();
    if (sys.includes('_fi') || sys.includes('fi_') || (campaign.geo && campaign.geo.toUpperCase() === 'FI')) return 'FI';
    if (sys.includes('_no') || sys.includes('no_') || (campaign.geo && campaign.geo.toUpperCase() === 'NO')) return 'NO';
    if (sys.includes('_de') || sys.includes('de_') || (campaign.geo && campaign.geo.toUpperCase() === 'DE')) return 'DE';
    return 'SE';
  }, [campaign]);

  const trackingLink = useMemo(() => {
    const htmlStr = typeof currentHtml === 'string' ? currentHtml : safeHtmlString(currentHtml);
    if (htmlStr) {
      const matchGo = htmlStr.match(/href=["'](https?:\/\/[^"']+\/go\/[^"']+)["']/i) || htmlStr.match(/(https?:\/\/[^\s"'<>\)\]]+\/go\/[^\s"'<>\)\]]+)/i);
      if (matchGo) return matchGo[1];
      const matchAny = htmlStr.match(/href=["'](https?:\/\/[^"']+)["']/i);
      if (matchAny && !matchAny[1].includes('konsumentverket')) return matchAny[1];
    }
    let cleanAdv = advertiser
      .replace(/å/gi, 'a')
      .replace(/ä/gi, 'a')
      .replace(/ö/gi, 'o')
      .replace(/ø/gi, 'o')
      .replace(/æ/gi, 'ae')
      .replace(/[^\w\s-]/g, '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-');
    if (cleanAdv === 'lanerad' || cleanAdv === 'laaneradd' || cleanAdv === 'lnerd') cleanAdv = 'lanerad';
    if (detectedGeo === 'NO') {
      if (cleanAdv === 'axo' || cleanAdv === 'axofinans') cleanAdv = 'axofinans-no';
      if (cleanAdv === 'uno-finans' || cleanAdv === 'unonans') cleanAdv = 'unofinans';
    } else if (detectedGeo === 'FI') {
      if (cleanAdv === 'zmarta') cleanAdv = 'zmarta-fi';
      if (cleanAdv === 'ferratum') cleanAdv = 'ferratum-fi';
      if (cleanAdv === 'rahalaitos') cleanAdv = 'rahalaitos-fi';
      if (cleanAdv === 'axolaina') cleanAdv = 'axolaina-fi';
      if (cleanAdv === 'sortter') cleanAdv = 'sortter-fi';
      if (cleanAdv === 'omalaina') cleanAdv = 'omalaina-fi';
    }
    const sysId = (campaign.systemId || campaign.espId || '').toLowerCase();
    const config = ESP_TRACKING_CONFIG[sysId] || {
      domain: detectedGeo === 'NO' ? 'provepakker.com' : detectedGeo === 'FI' ? 'parhaatedut.com' : detectedGeo === 'DE' ? 'sparfuechse-aktuell.de' : 'klarkredit24.se',
      pathPrefix: '/go/',
      s1: sysId,
      s2Macro: sysId.includes('fb') ? '<$LeadID$>' : sysId.includes('gr') ? '[[leadid]]' : sysId.includes('ms') ? '#recipient:LeadID#' : sysId.includes('ml') ? '{$leadid}' : '{{leadid}}'
    };

    const s3Val = (campaign.textEdition || 'TA1').toUpperCase();
    const s4Val = campaign.afimaCampaignId || campaign.campaignId || 'AC----';

    if (sysId === 'dp_se' || sysId.startsWith('dp_') || sysId.includes('drip')) {
      return `https://${config.domain}${config.pathPrefix}${cleanAdv}/?s1=${config.s1}&s2=${config.s2Macro}&s3=${s3Val}`;
    }

    return `https://${config.domain}${config.pathPrefix}${cleanAdv}/?s1=${config.s1}&s2=${config.s2Macro}&s3=${s3Val}&s4=${s4Val}`;
  }, [currentHtml, campaign, detectedGeo]);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-scheduler/generate-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignName: campaign.campaignName || campaign.name,
          existingSubject: subject,
          existingPreview: preview,
          advertiser,
          geo: detectedGeo,
          edition: campaign.textEdition || 't1',
          prompt
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setAlternatives(data.alternatives || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImprove = async () => {
    setIsImproving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-scheduler/improve-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          htmlContent: currentHtml,
          campaignName: campaign.campaignName || campaign.name,
          advertiser,
          geo: detectedGeo,
          prompt
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      if (data.improvedHtml) {
        setCurrentHtml(data.improvedHtml);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsImproving(false);
    }
  };

  const scoreBars = campaign.scoreBreakdown ? Object.entries(campaign.scoreBreakdown).map(([key, val]) => ({
    label: key,
    value: val as number
  })) : [];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col overflow-hidden relative" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50/80">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900">{campaign.campaignName || campaign.name}</h2>
            {campaign.textEdition && (
              <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-bold rounded uppercase">
                {campaign.textEdition}
              </span>
            )}
            <span className="text-xs font-mono bg-gray-200 text-gray-700 px-2 py-1 rounded">
              {campaign.systemId || campaign.espId}
            </span>
            {campaign.includeNL && (
              <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full">
                +NL
              </span>
            )}
            {campaign.dryRun && (
              <span className="px-2 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full">
                DRY RUN
              </span>
            )}
            {isManual && (
              <span className="px-2 py-1 bg-slate-200 text-slate-700 text-xs font-bold rounded-full">
                Manual Campaign
              </span>
            )}
            {!isManual && (campaign.status === 'pending' || campaign.status === 'approved') && onOpenSwap && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenSwap(campaign);
                }}
                className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-bold rounded-lg border border-indigo-200 flex items-center gap-1 shadow-2xs transition-colors cursor-pointer"
                title="Swap or Replace Campaign"
              >
                <ArrowLeftRight className="w-3 h-3 text-indigo-600" />
                Swap / Replace
              </button>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
          <div className="w-full md:w-1/2 p-6 overflow-y-auto border-r flex flex-col gap-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Send Time (CEST)</label>
                <input 
                  type="time" 
                  value={time}
                  onChange={e => setTime(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  disabled={isManual}
                />
              </div>
            </div>
            <div className="space-y-4">
              {/* 3-Pair Subject & Preview Rotation Selector */}
              {subjectPairs && subjectPairs.length > 0 && (
                <div className="bg-indigo-50/70 border border-indigo-100 rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-bold text-indigo-900 uppercase flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 text-indigo-600" />
                      Rotating Subject & Preview Pairs (3 Active Cycles)
                    </label>
                    <span className="text-[10px] text-indigo-600 font-medium">Auto-cycles on schedule</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {subjectPairs.map((pair: any, idx: number) => {
                      const isSelected = subject === pair.subject;
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            setSubject(pair.subject);
                            if (pair.previewText) setPreview(pair.previewText);
                          }}
                          className={`p-2 text-left rounded-lg border text-xs transition-all ${
                            isSelected
                              ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm font-semibold'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300'
                          }`}
                        >
                          <div className={`text-[10px] uppercase font-bold mb-0.5 ${isSelected ? 'text-indigo-100' : 'text-gray-400'}`}>
                            Pair #{idx + 1}
                          </div>
                          <div className="truncate font-medium">{pair.subject}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <label className="flex justify-between text-xs font-bold text-gray-500 uppercase mb-1">
                  Subject Line {isSubjectModified && <span className="text-indigo-600 font-medium normal-case">✏️ Modified</span>}
                </label>
                <input 
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  disabled={isManual}
                />
              </div>
              <div>
                <label className="flex justify-between text-xs font-bold text-gray-500 uppercase mb-1">
                  Preview Text {isPreviewModified && <span className="text-indigo-600 font-medium normal-case">✏️ Modified</span>}
                </label>
                <input 
                  type="text"
                  value={preview}
                  onChange={e => setPreview(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  disabled={isManual}
                />
              </div>
              <div>
                <label className="flex justify-between text-xs font-bold text-gray-500 uppercase mb-1">
                  Brand / Partner Name <span className="text-[10px] text-gray-400 font-normal">Used in disclaimers & benefit box headers</span>
                </label>
                <input 
                  type="text"
                  value={brandName}
                  onChange={e => setBrandName(e.target.value)}
                  placeholder="e.g. Sparklubben, Advisa, Lendo"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  disabled={isManual}
                />
              </div>
            </div>

            {/* Audience & Segmentation */}
            <div className="bg-gray-50 border rounded-xl p-4 space-y-3 shadow-inner">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-gray-700 uppercase flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-indigo-600" />
                  Audience & Segmentation
                </h3>
                {saveSuccess && <span className="text-emerald-600 text-xs font-semibold animate-pulse">✓ Saved</span>}
              </div>

              {/* Target List */}
              <div>
                <label className="block text-[11px] font-semibold text-gray-600 mb-1">Target Subscription List</label>
                <select 
                  value={listId} 
                  onChange={e => setListId(e.target.value)}
                  className="w-full px-3 py-1.5 text-xs border rounded-lg bg-white focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  disabled={isManual}
                >
                  {sysId === 'gr_se' ? (
                    <option value="ixQq9">Sverige Post (ixQq9) — Sparkredit SE</option>
                  ) : sysId === 'gr_se2' ? (
                    <option value="Pr1vv">Svenska Lån (Pr1vv) — Svenska Lån SE</option>
                  ) : sysId === 'gr_no' ? (
                    <option value="Xk5x1">Norske Tilbud (Xk5x1) — GetResponse NO</option>
                  ) : sysId === 'gr_fi' ? (
                    <option value="Zd2hu">Suomalaisetlainat (Zd2hu) — GetResponse FI</option>
                  ) : sysId === 'gr_de' ? (
                    <option value="jjrg7">post (jjrg7) — GetResponse DE</option>
                  ) : sysId.includes('_no') ? (
                    <option value="1128058">Onlinetilbud (1128058) — All Active NO Leads</option>
                  ) : sysId.includes('_fi') ? (
                    <option value="1121236">Pikalaina (1121236) — All Active FI Leads</option>
                  ) : sysId.includes('_de') ? (
                    <option value="1126000">FeedBlitz DE (1126000) — All Active DE Leads</option>
                  ) : (
                    <>
                      <option value="1110202">Svenska Erbjudanden (1110202) — All Active SE Leads</option>
                      <option value="1110204">Test list (1110204)</option>
                    </>
                  )}
                </select>
              </div>

              {/* GetResponse Sparkredit Mode: Entire List */}
              {isGRSparkredit && (
                <div className="bg-emerald-50 text-emerald-800 text-xs p-3 rounded-lg border border-emerald-200 space-y-1">
                  <div className="flex items-center gap-1.5 font-bold">
                    <span>⚡</span>
                    <span>Full List Broadcast (Entire Audience)</span>
                  </div>
                  <p className="text-[11px] text-emerald-700">
                    Sparkredit (<code>gr_se</code>) always broadcasts to 100% of subscribers on <strong>Sverige Post</strong> (<code>ixQq9</code>). No segment filter or exclusion rules applied.
                  </p>
                </div>
              )}

              {/* Other GetResponse Accounts Mode: SendSegment */}
              {isGR && !isGRSparkredit && (
                <div className="bg-blue-50 text-blue-800 text-xs p-3 rounded-lg border border-blue-200 space-y-1">
                  <div className="flex items-center gap-1.5 font-bold">
                    <span>🎯</span>
                    <span>Target Segment: <code>SendSegment</code></span>
                  </div>
                  <p className="text-[11px] text-blue-700">
                    Sending to active engaged subscribers in <strong>SendSegment</strong> on this account.
                  </p>
                </div>
              )}

              {/* FeedBlitz Segmentation & Suppression Controls */}
              {!isGR && (
                <>
                  {/* Gender Targeting */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-[11px] font-semibold text-gray-600">Gender Segment Filter</label>
                      <div className="flex gap-1 text-[10px]">
                        <button 
                          type="button" 
                          onClick={() => setGenderSegment(['M', 'F', 'U'])} 
                          className={`px-1.5 py-0.5 rounded font-medium border transition-colors ${genderSegment.length === 3 ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                        >
                          All (M/F/U)
                        </button>
                        <button 
                          type="button" 
                          onClick={() => setGenderSegment(['F'])} 
                          className={`px-1.5 py-0.5 rounded font-medium border transition-colors ${genderSegment.length === 1 && genderSegment[0] === 'F' ? 'bg-pink-600 text-white border-pink-600' : 'bg-pink-50 text-pink-700 hover:bg-pink-100'}`}
                        >
                          ♀ Female Only
                        </button>
                        <button 
                          type="button" 
                          onClick={() => setGenderSegment(['M', 'U'])} 
                          className={`px-1.5 py-0.5 rounded font-medium border transition-colors ${genderSegment.length === 2 && genderSegment.includes('M') && genderSegment.includes('U') ? 'bg-blue-600 text-white border-blue-600' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                        >
                          ♂ Male + Undef
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 bg-white p-2 rounded-lg border text-xs">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={genderSegment.includes('M')} 
                          onChange={e => {
                            if (e.target.checked) setGenderSegment([...genderSegment, 'M']);
                            else setGenderSegment(genderSegment.filter(g => g !== 'M'));
                          }} 
                          className="rounded text-indigo-600 focus:ring-indigo-500"
                        />
                        <span>Male (M)</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={genderSegment.includes('F')} 
                          onChange={e => {
                            if (e.target.checked) setGenderSegment([...genderSegment, 'F']);
                            else setGenderSegment(genderSegment.filter(g => g !== 'F'));
                          }} 
                          className="rounded text-pink-600 focus:ring-pink-500"
                        />
                        <span>Female (F)</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={genderSegment.includes('U')} 
                          onChange={e => {
                            if (e.target.checked) setGenderSegment([...genderSegment, 'U']);
                            else setGenderSegment(genderSegment.filter(g => g !== 'U'));
                          }} 
                          className="rounded text-gray-600 focus:ring-gray-500"
                        />
                        <span>Undefined (U)</span>
                      </label>
                    </div>

                    {/* Audience Split Banner */}
                    {genderSegment.length === 1 && genderSegment[0] === 'F' && (
                      <div className="mt-2 bg-pink-50 text-pink-800 text-[11px] p-2 rounded border border-pink-200 flex items-start gap-1.5">
                        <span>💡</span>
                        <span><strong>Audience Split:</strong> Sending to Female only. Male + Undefined audience is free for a paired sendout in this slot.</span>
                      </div>
                    )}
                    {genderSegment.length === 2 && genderSegment.includes('M') && genderSegment.includes('U') && (
                      <div className="mt-2 bg-blue-50 text-blue-800 text-[11px] p-2 rounded border border-blue-200 flex items-start gap-1.5">
                        <span>💡</span>
                        <span><strong>Audience Split:</strong> Sending to Male + Undefined only. Female audience is free for a paired sendout in this slot.</span>
                      </div>
                    )}
                  </div>

                  {/* Exclude Blacklist / Suppression Segments */}
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                      Exclude Blacklist / Suppression Segment(s)
                    </label>
                    {(campaign.systemId === 'feedblitz_se' || campaign.systemId === 'fb_se' || (!campaign.systemId && !isGR)) ? (
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-2 text-xs bg-white p-2 rounded-lg border cursor-pointer hover:bg-gray-50">
                          <input 
                            type="checkbox"
                            checked={excludedSegments.includes('17051')}
                            onChange={e => {
                              if (e.target.checked) setExcludedSegments([...excludedSegments, '17051']);
                              else setExcludedSegments(excludedSegments.filter(s => s !== '17051'));
                            }}
                            className="rounded text-red-600 focus:ring-red-500"
                          />
                          <div className="flex-1 flex items-center justify-between">
                            <span className="font-medium text-gray-800">Axo_Exclude <span className="text-gray-400 font-mono text-[10px]">(17051)</span></span>
                            {['axo', 'zmarta', 'freedom'].some(k => (campaign.campaignName || '').toLowerCase().includes(k)) && (
                              <span className="text-[10px] bg-red-100 text-red-700 font-bold px-1.5 py-0.5 rounded">
                                🛡️ Required for Axo Group
                              </span>
                            )}
                          </div>
                        </label>

                        <label className="flex items-center gap-2 text-xs bg-white p-2 rounded-lg border cursor-pointer hover:bg-gray-50">
                          <input 
                            type="checkbox"
                            checked={excludedSegments.includes('12334')}
                            onChange={e => {
                              if (e.target.checked) setExcludedSegments([...excludedSegments, '12334']);
                              else setExcludedSegments(excludedSegments.filter(s => s !== '12334'));
                            }}
                            className="rounded text-red-600 focus:ring-red-500"
                          />
                          <div className="flex-1 flex items-center justify-between">
                            <span className="font-medium text-gray-800">Nordic_Leads <span className="text-gray-400 font-mono text-[10px]">(12334)</span></span>
                            {!campaign.includeNL ? (
                              <span className="text-[10px] bg-amber-100 text-amber-800 font-bold px-1.5 py-0.5 rounded">
                                Excluded (Non-NL Afternoon Slot)
                              </span>
                            ) : (
                              <span className="text-[10px] bg-purple-100 text-purple-700 font-bold px-1.5 py-0.5 rounded">
                                Included in +NL Slot
                              </span>
                            )}
                          </div>
                        </label>

                        <label className="flex items-center gap-2 text-xs bg-white p-2 rounded-lg border cursor-pointer hover:bg-gray-50">
                          <input 
                            type="checkbox"
                            checked={excludedSegments.includes('27774')}
                            onChange={e => {
                              if (e.target.checked) setExcludedSegments([...excludedSegments, '27774']);
                              else setExcludedSegments(excludedSegments.filter(s => s !== '27774'));
                            }}
                            className="rounded text-red-600 focus:ring-red-500"
                          />
                          <div className="flex-1 flex items-center justify-between">
                            <span className="font-medium text-gray-800">Inactive_ManualUnsubs_1609 <span className="text-gray-400 font-mono text-[10px]">(27774)</span></span>
                          </div>
                        </label>
                      </div>
                    ) : (
                      <div className="text-[11px] text-gray-500 italic p-2.5 bg-gray-50 rounded-lg border">
                        All active subscribers on list <strong>{listId}</strong> are targeted directly. No list suppressions required.
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="pt-1 flex justify-end">
                <button 
                  onClick={handleSaveSettings}
                  disabled={isSaving}
                  className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50 flex items-center gap-1"
                >
                  <Save className="w-3.5 h-3.5" />
                  {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>

            {!isManual && (
              <div className="bg-indigo-50 p-4 rounded-xl space-y-4 border border-indigo-100">
                <h3 className="text-sm font-bold text-indigo-900 flex items-center gap-2">✨ AI Suggestions</h3>
                <input 
                  type="text"
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder="Custom prompt/direction (e.g. 'Make it more urgent')"
                  className="w-full px-3 py-2 border border-indigo-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
                <div className="flex gap-2">
                  <button 
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="flex-1 bg-white border border-indigo-200 text-indigo-700 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isGenerating ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Generate Subjects'}
                  </button>
                  <button 
                    onClick={handleImprove}
                    disabled={isImproving || !currentHtml}
                    className="flex-1 bg-white border border-indigo-200 text-indigo-700 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isImproving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Improve Text'}
                  </button>
                </div>
                {error && <div className="text-red-500 text-xs font-medium">{error}</div>}
                {alternatives.length > 0 && (
                  <div className="space-y-2 mt-4">
                    {alternatives.map((alt, i) => (
                      <div 
                        key={i} 
                        className="bg-white border border-indigo-200 p-3 rounded-lg cursor-pointer hover:bg-indigo-50 transition-colors"
                        onClick={() => { setSubject(alt.subject); setPreview(alt.previewText); }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-bold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded">{alt.angle}</span>
                        </div>
                        <div className="text-sm font-medium text-gray-900">{alt.subject}</div>
                        <div className="text-xs text-gray-500">{alt.previewText}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {scoreBars.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-xs font-bold text-gray-500 uppercase">Score Breakdown</h3>
                <div className="space-y-2">
                  {scoreBars.map(bar => (
                    <div key={bar.label} className="flex items-center gap-2 text-xs">
                      <span className="w-24 truncate text-gray-600">{bar.label}</span>
                      <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden flex">
                        <div className="h-full bg-indigo-500" style={{ width: `${Math.max(0, Math.min(100, bar.value))}%` }} />
                      </div>
                      <span className="w-8 text-right font-medium">{Math.round(bar.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {campaign.reasoning && (
              <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-600 border italic">{campaign.reasoning}</div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-gray-50 p-2 rounded-lg border text-center">
                <div className="text-[10px] text-gray-500 font-bold uppercase">Open Rate</div>
                <div className="font-medium">{campaign.openRate != null ? `${campaign.openRate}%` : '—'}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg border text-center">
                <div className="text-[10px] text-gray-500 font-bold uppercase">Delivered</div>
                <div className="font-medium">{campaign.delivered != null ? campaign.delivered.toLocaleString() : '—'}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg border text-center">
                <div className="text-[10px] text-gray-500 font-bold uppercase">Unsubs</div>
                <div className="font-medium">{campaign.unsubs != null ? campaign.unsubs : '—'}</div>
              </div>
            </div>
          </div>
          <div className="w-full md:w-1/2 bg-gray-100 flex flex-col p-4 border-l overflow-hidden">
            {/* Quick Copy Box */}
            <div className={`mb-3 p-3 rounded-xl space-y-2 border ${
              isDrip ? 'bg-pink-50/90 border-pink-200' : 'bg-white border-gray-200 shadow-2xs'
            }`}>
              <div className="flex items-center justify-between">
                <span className={`text-xs font-bold flex items-center gap-1.5 ${
                  isDrip ? 'text-pink-900' : 'text-gray-700'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${isDrip ? 'bg-pink-500 animate-pulse' : 'bg-indigo-500'}`} />
                  {isDrip ? '📋 Copy Campaign Data for Drip UI' : '📋 Quick Copy Content'}
                </span>
                <span className={`text-[10px] font-medium ${isDrip ? 'text-pink-700' : 'text-gray-500'}`}>
                  {isDrip ? 'Create broadcast in Drip, then click Confirm below' : '1-click copy'}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => handleCopyText(subject, 'subject')}
                  className="px-2.5 py-1.5 bg-white hover:bg-gray-50 text-gray-800 text-xs font-semibold rounded-lg border border-gray-200 shadow-2xs transition-colors flex items-center justify-center gap-1 cursor-pointer"
                >
                  {copiedField === 'subject' ? '✓ Copied!' : '📋 Copy Subject'}
                </button>
                <button
                  type="button"
                  onClick={() => handleCopyText(preview, 'preview')}
                  className="px-2.5 py-1.5 bg-white hover:bg-gray-50 text-gray-800 text-xs font-semibold rounded-lg border border-gray-200 shadow-2xs transition-colors flex items-center justify-center gap-1 cursor-pointer"
                >
                  {copiedField === 'preview' ? '✓ Copied!' : '📋 Copy Preheader'}
                </button>
                <button
                  type="button"
                  onClick={() => handleCopyText(currentHtml, 'html')}
                  className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg shadow-2xs transition-colors flex items-center justify-center gap-1 cursor-pointer ${
                    isDrip
                      ? 'bg-pink-600 hover:bg-pink-700 text-white'
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  }`}
                >
                  {copiedField === 'html' ? '✓ Copied HTML!' : '📋 Copy Full HTML'}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold text-gray-500 uppercase">HTML Preview</h3>
              <div className="flex items-center gap-2">
                <button 
                  onClick={async () => {
                    try {
                      setIsImproving(true);
                      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/auto-repair`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ afimaCampaignId: campaign.afimaCampaignId }),
                      });
                      if (res.ok) {
                        const data = await res.json();
                        if (data.success) {
                          if (data.htmlContent) setCurrentHtml(data.htmlContent);
                          if (data.item?.subjectLine) setSubject(data.item.subjectLine);
                          if (data.item?.previewText) setPreview(data.item.previewText);
                          if (data.item?.brandName) setBrandName(data.item.brandName);
                          // Don't call onSaveSuccess here — it re-fetches the plan and re-mounts
                          // the component, which would overwrite the repair result.
                        } else if (data.error) {
                          alert(`Repair failed: ${data.error}`);
                        }
                      }
                    } catch (e: any) {
                      alert(`Repair failed: ${e.message}`);
                    } finally {
                      setIsImproving(false);
                    }
                  }}
                  className="text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-1 rounded shadow-sm hover:bg-emerald-100 transition-colors flex items-center gap-1"
                  disabled={isManual || isImproving}
                >
                  <Sparkles className="w-3 h-3" />
                  {isImproving ? 'Repairing...' : '⚡ Fix from Library'}
                </button>
                <button 
                  onClick={() => setHtmlEditMode(!htmlEditMode)}
                  className="text-xs font-semibold bg-white border px-2 py-1 rounded shadow-sm hover:bg-gray-50 transition-colors"
                  disabled={isManual}
                >
                  ✏️ Edit HTML
                </button>
              </div>
            </div>
            <div className="flex-1 bg-white rounded-lg border shadow-sm overflow-hidden flex flex-col">
              {htmlEditMode && !isManual ? (
                <textarea 
                  value={currentHtml}
                  onChange={e => setCurrentHtml(e.target.value)}
                  className="flex-1 w-full p-4 font-mono text-xs resize-none focus:outline-none"
                />
              ) : (
                <iframe 
                  srcDoc={currentHtml || '<div style="padding:20px;color:#888;font-family:sans-serif;text-align:center;">No HTML content available</div>'} 
                  sandbox="allow-same-origin"
                  className="w-full h-full border-none"
                  title="Email Preview"
                />
              )}
            </div>
            <div className="mt-4 space-y-2">
              <div className="bg-gray-900 text-green-400 font-mono text-[10px] p-2 rounded flex items-center justify-between shadow-inner overflow-hidden">
                <span className="truncate">{trackingLink}</span>
              </div>
              <div className="bg-white border rounded p-2 text-[10px] flex justify-between text-gray-500 shadow-sm">
                <span><strong className="text-gray-700">s1:</strong> {isDrip ? 'dpse' : 'ESP ID'}</span>
                <span><strong className="text-gray-700">s2:</strong> {isDrip ? '{{ subscriber.LeadID }}' : 'leadid'}</span>
                <span><strong className="text-gray-700">s3:</strong> text ed</span>
                <span><strong className="text-gray-700">s4:</strong> {isDrip ? '(omitted for reusable Drip templates)' : 'send ID'}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between gap-3">
          {onUnschedule && (
            <button
              onClick={() => onUnschedule(campaign)}
              className="px-3.5 py-2 bg-red-50 hover:bg-red-100 text-red-700 font-semibold rounded-lg transition-colors text-sm border border-red-200 flex items-center gap-1.5 shadow-xs cursor-pointer"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
              Delete / Unschedule from ESP
            </button>
          )}
          {!isManual && (campaign.status === 'pending' || campaign.status === 'approved') && onOpenSwap && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onOpenSwap(campaign);
              }}
              className="px-3.5 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold rounded-lg transition-colors text-sm border border-indigo-200 flex items-center gap-1.5 shadow-xs cursor-pointer"
            >
              <ArrowLeftRight className="w-4 h-4 text-indigo-600" />
              Swap / Replace Slot
            </button>
          )}
          <div className="flex items-center gap-2 ml-auto">
            {!isManual && campaign.status === 'pending' && (
              <>
                <button 
                  onClick={async () => {
                    const reason = window.prompt('Reject reason?');
                    if (reason) {
                      await onAction('reject', { afimaCampaignId: campaign.afimaCampaignId, reason });
                      onClose();
                    }
                  }}
                  className="px-4 py-2 bg-red-100 text-red-700 font-semibold rounded-lg hover:bg-red-200 transition-colors text-sm"
                >
                  ✖ Reject
                </button>
                <button 
                  onClick={async () => {
                    await onAction('approve', { afimaCampaignId: campaign.afimaCampaignId });
                    onClose();
                  }}
                  className="px-4 py-2 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors shadow-sm text-sm"
                >
                  ✅ Approve
                </button>
              </>
            )}
            {!isManual && campaign.status === 'approved' && (
              <button 
                onClick={async () => {
                  const reason = window.prompt('Hold reason?');
                  if (reason) {
                    await onAction('hold', { afimaCampaignId: campaign.afimaCampaignId, reason });
                    onClose();
                  }
                }}
                className="px-4 py-2 bg-orange-100 text-orange-700 font-semibold rounded-lg hover:bg-orange-200 transition-colors text-sm"
              >
                ⏸ Hold
              </button>
            )}
            {isDrip && (
              <button 
                type="button"
                onClick={handleConfirmDrip}
                disabled={confirmingDrip}
                className={`px-4 py-2 font-semibold rounded-lg shadow-sm text-sm flex items-center gap-1.5 transition-colors cursor-pointer ${
                  campaign.status === 'dispatched' || campaign.status === 'scheduled'
                    ? 'bg-slate-700 hover:bg-slate-800 text-white'
                    : 'bg-pink-600 hover:bg-pink-700 text-white'
                }`}
              >
                {confirmingDrip ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                {campaign.status === 'dispatched' || campaign.status === 'scheduled'
                  ? '✓ Confirmed Scheduled in Drip'
                  : 'Confirm Scheduled in Drip'}
              </button>
            )}
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-white border text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors shadow-sm text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SwapCampaignModal({
  item,
  weekPlan,
  espId,
  onClose,
  onSwapSlots,
  onReplaceCampaign,
}: {
  item: PendingItem;
  weekPlan: WeekPlan | null;
  espId: string;
  onClose: () => void;
  onSwapSlots: (itemA: PendingItem, itemB: PendingItem) => void;
  onReplaceCampaign: (afimaId: string, campaignName: string, textEdition: string) => void;
}) {
  const [activeTab, setActiveTab] = useState<'swap' | 'replace'>('swap');
  const [candidates, setCandidates] = useState<any[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Resolve specific ESP for this item (e.g. feedblitz_no, gr_no, moosend_se, etc.) even when dashboard is on all_no / all_se
  const effectiveEspId = (item.systemId || (item as any).espId || espId);
  const esps = getAvailableEsps(espId);
  const targetEspObj = esps.find(e => e.id === effectiveEspId || e.id.startsWith(effectiveEspId.split('_')[0]));
  const espBadgeName = targetEspObj ? targetEspObj.shortName : effectiveEspId.toUpperCase();

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Fetch candidates from library for the item's specific ESP
  useEffect(() => {
    if (activeTab === 'replace') {
      setLoadingCandidates(true);
      fetch(`${API_BASE_URL}/api/auto-schedule/campaign-candidates?espId=${effectiveEspId}`)
        .then(res => res.json())
        .then(data => {
          setCandidates(data.candidates || []);
        })
        .catch(err => console.error('Failed to load candidates:', err))
        .finally(() => setLoadingCandidates(false));
    }
  }, [activeTab, effectiveEspId]);

  // Other scheduled slots in the current plan sorted chronologically
  const otherSlots = useMemo(() => {
    if (!weekPlan?.items) return [];
    return weekPlan.items
      .filter(i => i.afimaCampaignId !== item.afimaCampaignId)
      .sort((a, b) => (a.scheduledDate || '').localeCompare(b.scheduledDate || '') || (a.slotTime || '').localeCompare(b.slotTime || ''));
  }, [weekPlan, item]);

  // Filter candidates by search query
  const filteredCandidates = useMemo(() => {
    if (!searchQuery) return candidates;
    const q = searchQuery.toLowerCase();
    return candidates.filter(c => 
      c.advertiser.toLowerCase().includes(q) ||
      c.texts?.some((t: any) => (t.subject || '').toLowerCase().includes(q) || (t.name || '').toLowerCase().includes(q))
    );
  }, [candidates, searchQuery]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden relative" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-indigo-600" />
              Swap / Replace Campaign
            </h3>
            <div className="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
              <span>Selected: <strong className="text-gray-800">{item.campaignName}</strong> ({item.scheduledDate ? item.scheduledDate.slice(0, 10) : ''} at {item.slotTime})</span>
              <span className={`px-2 py-0.5 text-white text-[10px] font-bold rounded-full ${targetEspObj?.badgeBg || 'bg-indigo-600'}`}>
                {espBadgeName} (Letter {targetEspObj?.letter || 'A'})
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b bg-gray-100/60 px-6 pt-2 gap-2">
          <button
            onClick={() => setActiveTab('swap')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${
              activeTab === 'swap'
                ? 'border-indigo-600 text-indigo-600 bg-white rounded-t-lg shadow-sm'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            ⇄ Swap with Scheduled Slot ({otherSlots.length})
          </button>
          <button
            onClick={() => setActiveTab('replace')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${
              activeTab === 'replace'
                ? 'border-indigo-600 text-indigo-600 bg-white rounded-t-lg shadow-sm'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            🔄 Replace Campaign from Library
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'swap' ? (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 mb-3">
                Click any slot below to trade scheduled dates and times with <strong>{item.campaignName}</strong>:
              </p>
              {otherSlots.map(target => (
                <div
                  key={target.afimaCampaignId}
                  className="p-3 border rounded-xl hover:bg-indigo-50/50 hover:border-indigo-300 transition-all cursor-pointer flex items-center justify-between group"
                  onClick={() => {
                    onSwapSlots(item, target);
                    onClose();
                  }}
                >
                  <div>
                    <div className="font-semibold text-gray-900 text-sm group-hover:text-indigo-600">
                      {target.campaignName}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                      <span>📅 {target.scheduledDate ? target.scheduledDate.slice(0, 10) : ''}</span>
                      <span>⏰ {target.slotTime}</span>
                      {target.includeNL && <span className="px-1 py-0.5 bg-purple-100 text-purple-700 text-[10px] rounded font-bold">+NL</span>}
                    </div>
                  </div>
                  <button className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity shadow-sm">
                    Swap Here
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                <input
                  type="text"
                  placeholder="Search library by advertiser or keyword (e.g. Sambla, Nutra, Lendo)..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              {loadingCandidates ? (
                <div className="p-8 text-center text-gray-500 flex flex-col items-center">
                  <RefreshCw className="w-6 h-6 animate-spin text-indigo-500 mb-2" />
                  Loading library campaigns...
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredCandidates.map(cand => {
                    const validTexts = filterTextsForEsp(cand.texts, effectiveEspId);
                    if (!validTexts || validTexts.length === 0) return null;
                    return (
                    <div key={cand.advertiser} className="border rounded-xl p-3 bg-gray-50/50">
                      <div className="font-bold text-gray-900 text-sm uppercase tracking-wide mb-2 flex items-center justify-between">
                        <span>{cand.advertiser}</span>
                        <span className="text-xs text-indigo-600 font-semibold bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100">{validTexts.length} eligible versions ({espBadgeName})</span>
                      </div>
                      <div className="space-y-1.5">
                        {validTexts.map((t: any) => {
                          const edCode = String(t.textId || t.versionId || t.name || 't1').toUpperCase();
                          return (
                          <div
                            key={t.textId || t.versionId}
                            className="bg-white p-2.5 rounded-lg border hover:border-indigo-400 hover:bg-indigo-50/30 transition-all flex items-center justify-between cursor-pointer group"
                            onClick={() => {
                              const displayName = `${cand.advertiser.charAt(0).toUpperCase() + cand.advertiser.slice(1)} ${edCode}`;
                              onReplaceCampaign(item.afimaCampaignId, displayName, edCode.toLowerCase());
                              onClose();
                            }}
                          >
                            <div className="flex-1 pr-3 truncate">
                              <div className="flex items-center gap-2">
                                <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-bold rounded uppercase">
                                  {edCode}
                                </span>
                                <span className="text-xs font-semibold text-gray-800 truncate">{t.subject || t.name}</span>
                              </div>
                              {(t.preview || t.previewText) && <p className="text-[11px] text-gray-500 truncate mt-0.5">{t.preview || t.previewText}</p>}
                            </div>
                            <button className="px-2.5 py-1 bg-emerald-600 text-white text-xs font-semibold rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                              Select
                            </button>
                          </div>
                        );})}
                      </div>
                    </div>
                  );})}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-gray-50 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-white border text-gray-700 font-semibold rounded-lg hover:bg-gray-50 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function AuditFailsafeModal({
  auditReport,
  isRepairing,
  onClose,
  onAutoRepairAll,
  onAutoRepairSingle,
  onApproveValidOnly
}: {
  auditReport: any;
  isRepairing: boolean;
  onClose: () => void;
  onAutoRepairAll: () => Promise<void>;
  onAutoRepairSingle: (id: string) => Promise<void>;
  onApproveValidOnly: () => Promise<void>;
}) {
  const defectiveItems = auditReport?.items?.filter((i: any) => !i.isValid) || [];
  const validItems = auditReport?.items?.filter((i: any) => i.isValid) || [];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] shadow-2xl flex flex-col overflow-hidden border border-red-200">
        {/* Header */}
        <div className="bg-red-50/80 border-b border-red-100 p-5 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-red-100 text-red-700 rounded-xl">
              <ShieldAlert className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                Quality & Safety Pre-Flight Failsafe
              </h2>
              <p className="text-xs text-red-700 mt-0.5">
                Found <strong>{defectiveItems.length} campaign(s)</strong> with missing content or broken links that cannot be approved in their current state.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white/80 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="space-y-3">
            <div className="text-xs font-bold uppercase tracking-wider text-red-800 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
              Action Required ({defectiveItems.length})
            </div>

            {defectiveItems.map((item: any) => (
              <div key={item.afimaCampaignId} className="border border-red-200 bg-red-50/30 rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-gray-900">{item.campaignName}</span>
                    <span className="text-xs text-gray-500">⏰ {item.slotTime || 'Scheduled'}</span>
                  </div>
                  <button
                    onClick={() => onAutoRepairSingle(item.afimaCampaignId)}
                    disabled={isRepairing}
                    className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm flex items-center gap-1.5 transition-colors disabled:opacity-50"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {isRepairing ? 'Repairing...' : 'Auto-Repair'}
                  </button>
                </div>

                <div className="space-y-1 pt-1">
                  {item.issues?.map((iss: string, idx: number) => (
                    <div key={idx} className="flex items-start gap-1.5 text-xs text-red-700">
                      <span className="text-red-500 font-bold">•</span>
                      <span>{iss}</span>
                    </div>
                  ))}
                  {item.warnings?.map((w: string, idx: number) => (
                    <div key={idx} className="flex items-start gap-1.5 text-xs text-amber-700">
                      <span className="text-amber-500 font-bold">•</span>
                      <span>⚠️ {w}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {validItems.length > 0 && (
            <div className="pt-3 border-t">
              <div className="text-xs font-bold uppercase tracking-wider text-emerald-800 flex items-center gap-1.5 mb-2">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                Ready for Dispatch ({validItems.length})
              </div>
              <div className="grid grid-cols-2 gap-2">
                {validItems.map((item: any) => (
                  <div key={item.afimaCampaignId} className="p-2 border border-emerald-100 bg-emerald-50/40 rounded-lg flex items-center justify-between text-xs">
                    <span className="font-semibold text-gray-800 truncate">{item.campaignName}</span>
                    <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">100% OK</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
          <button onClick={onClose} className="px-4 py-2 bg-white border text-gray-700 font-semibold rounded-lg hover:bg-gray-50 text-sm">
            Cancel
          </button>
          <div className="flex gap-2">
            {validItems.length > 0 && (
              <button
                onClick={onApproveValidOnly}
                disabled={isRepairing}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold rounded-lg text-sm transition-colors disabled:opacity-50"
              >
                Approve Valid Only ({validItems.length})
              </button>
            )}
            <button
              onClick={onAutoRepairAll}
              disabled={isRepairing}
              className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg text-sm shadow-md flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              <Sparkles className="w-4 h-4" />
              {isRepairing ? 'Repairing from Library...' : '⚡ Auto-Repair All & Approve'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ManualAddCampaignModal({
  isOpen,
  initialDate,
  espId,
  weekId,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  initialDate: string;
  espId: string;
  weekId: string;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}) {
  const [date, setDate] = useState(initialDate || new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState('17:00');
  const [status, setStatus] = useState<'pending' | 'approved'>('pending');
  const [candidates, setCandidates] = useState<any[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<any | null>(null);
  const [selectedEdition, setSelectedEdition] = useState('t1');
  const [subject, setSubject] = useState('');
  const [preview, setPreview] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const availableEsps = useMemo(() => getAvailableEsps(espId), [espId]);
  const [selectedEsp, setSelectedEsp] = useState<string>(() => {
    return availableEsps[0]?.id || espId;
  });

  useEffect(() => {
    if (initialDate) setDate(initialDate);
  }, [initialDate]);

  useEffect(() => {
    if (isOpen) {
      const esps = getAvailableEsps(espId);
      const defaultEsp = esps[0]?.id || espId;
      setSelectedEsp(defaultEsp);
      setLoadingCandidates(true);
      fetch(`${API_BASE_URL}/api/auto-schedule/campaign-candidates?espId=${espId}`)
        .then(res => res.json())
        .then(data => {
          const list = data.candidates || [];
          setCandidates(list);
          if (list.length > 0) {
            setSelectedCandidate(list[0]);
            const validTexts = filterTextsForEsp(list[0].texts, defaultEsp);
            const firstEd = validTexts?.[0] || list[0].texts?.[0];
            if (firstEd) {
              const edName = firstEd.versionId || firstEd.name || 't1';
              setSelectedEdition(edName);
              setSubject(firstEd.subject || '');
              setPreview(firstEd.previewText || firstEd.preview || '');
            }
          }
        })
        .catch(err => setError(`Failed to load campaigns: ${err.message}`))
        .finally(() => setLoadingCandidates(false));
    }
  }, [isOpen, espId]);

  const handleSelectCandidate = (cand: any) => {
    setSelectedCandidate(cand);
    const validTexts = filterTextsForEsp(cand.texts, selectedEsp);
    const firstText = validTexts?.[0] || cand.texts?.[0];
    if (firstText) {
      const edName = firstText.versionId || firstText.name || 't1';
      setSelectedEdition(edName);
      setSubject(firstText.subject || '');
      setPreview(firstText.previewText || firstText.preview || '');
    } else {
      setSelectedEdition('t1');
      setSubject('');
      setPreview('');
    }
  };

  const handleSelectEsp = (newEspId: string) => {
    setSelectedEsp(newEspId);
    if (selectedCandidate) {
      const validTexts = filterTextsForEsp(selectedCandidate.texts, newEspId);
      const firstText = validTexts?.[0] || selectedCandidate.texts?.[0];
      if (firstText) {
        const edName = firstText.versionId || firstText.name || 't1';
        setSelectedEdition(edName);
        setSubject(firstText.subject || '');
        setPreview(firstText.previewText || firstText.preview || '');
      }
    }
  };

  const handleSelectEdition = (edStr: string) => {
    setSelectedEdition(edStr);
    const textObj = selectedCandidate?.texts?.find((t: any) => (t.versionId || t.name || '').toLowerCase() === edStr.toLowerCase());
    if (textObj) {
      if (textObj.subject) setSubject(textObj.subject);
      if (textObj.previewText || textObj.preview) setPreview(textObj.previewText || textObj.preview);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCandidate) {
      setError('Please select a campaign');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const scheduledDateTime = `${date}T${time}:00`;
      const targetDt = new Date(scheduledDateTime);
      if (targetDt.getTime() <= Date.now() - 60000) {
        throw new Error('Cannot schedule in the past. Please select a future date and time.');
      }
      const campName = `${selectedCandidate.advertiser} ${selectedEdition.toUpperCase()}`;
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/add-item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          espId: selectedEsp,
          scheduledDate: scheduledDateTime,
          slotTime: time,
          campaignName: campName,
          textEdition: selectedEdition,
          subject: subject.trim() || undefined,
          preview: preview.trim() || undefined,
          status,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      const espLabel = availableEsps.find(e => e.id === selectedEsp)?.shortName || selectedEsp;
      onSuccess(`✅ Added "${campName}" (${espLabel}) to ${date} at ${time}`);
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCandidates = useMemo(() => {
    if (!searchQuery) return candidates;
    const q = searchQuery.toLowerCase();
    return candidates.filter(c => 
      c.advertiser.toLowerCase().includes(q) ||
      (c.category || '').toLowerCase().includes(q) ||
      c.texts?.some((t: any) => (t.subject || '').toLowerCase().includes(q) || (t.name || '').toLowerCase().includes(q))
    );
  }, [candidates, searchQuery]);

  if (!isOpen) return null;

  const currentAvailableTexts = selectedCandidate ? filterTextsForEsp(selectedCandidate.texts, selectedEsp) : [];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] flex flex-col overflow-hidden relative" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-indigo-50 to-purple-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-indigo-600 text-white rounded-lg shadow-sm">
              <Plus className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Add Campaign Manually</h3>
              <p className="text-xs text-gray-500">Add a campaign from the library to a specific date & slot</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Target ESP Selector (when in multi-country mode or availableEsps > 1) */}
          {availableEsps.length > 1 && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Target ESP Channel
                </label>
                <span className="text-[11px] text-indigo-600 font-semibold">
                  Select ESP to filter matching text copies
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {availableEsps.map(esp => {
                  const isSelected = selectedEsp === esp.id;
                  return (
                    <button
                      key={esp.id}
                      type="button"
                      onClick={() => handleSelectEsp(esp.id)}
                      className={`p-2 rounded-lg text-xs font-semibold border transition-all flex items-center justify-between cursor-pointer ${
                        isSelected
                          ? `ring-2 ring-indigo-600 bg-indigo-50/80 border-indigo-600 text-indigo-950 font-bold shadow-xs`
                          : `bg-white hover:bg-gray-100/80 border-gray-200 text-gray-700`
                      }`}
                    >
                      <div className="flex items-center gap-1.5 truncate">
                        <span className={`w-2 h-2 rounded-full ${esp.badgeBg}`} />
                        <span className="truncate">{esp.shortName}</span>
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono font-bold ${
                        isSelected ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {esp.letter}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Date</label>
              <input
                type="date"
                min={new Date().toISOString().slice(0, 10)}
                value={date}
                onChange={e => setDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Time (CEST)</label>
              <div className="flex gap-1.5">
                <input
                  type="time"
                  value={time}
                  onChange={e => setTime(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  required
                />
              </div>
            </div>
          </div>

          {/* Quick Time Presets */}
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-gray-400 uppercase">Presets:</span>
            {['08:00', '10:00', '16:00', '17:00'].map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setTime(t)}
                className={`px-2.5 py-1 rounded text-xs font-semibold transition-all ${
                  time === t 
                    ? 'bg-indigo-600 text-white shadow-xs' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Campaign Selector */}
          <div>
            <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Select Campaign / Advertiser</label>
            <div className="relative mb-2">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 border rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
            </div>
            
            {loadingCandidates ? (
              <div className="p-4 text-center text-xs text-gray-400">Loading campaigns...</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-1 border rounded-lg bg-gray-50/50">
                {filteredCandidates.map((c: any) => {
                  const isSelected = selectedCandidate?.campaignId === c.campaignId || selectedCandidate?.advertiser === c.advertiser;
                  const matchingCount = filterTextsForEsp(c.texts, selectedEsp).length;
                  return (
                    <button
                      key={c.campaignId || c.advertiser}
                      type="button"
                      onClick={() => handleSelectCandidate(c)}
                      className={`p-2 rounded-lg text-left border transition-all ${
                        isSelected 
                          ? 'border-indigo-600 bg-indigo-50/80 text-indigo-950 font-bold shadow-xs' 
                          : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      <div className="text-xs font-bold truncate">{c.advertiser}</div>
                      <div className="text-[10px] text-gray-400 capitalize truncate flex items-center justify-between mt-0.5">
                        <span>{c.category || 'Loan'}</span>
                        <span className="font-semibold text-indigo-600">{matchingCount} texts</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Text Edition Selector */}
          {selectedCandidate && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-bold text-gray-600 uppercase">
                  Creative / Text Edition ({availableEsps.find(e => e.id === selectedEsp)?.shortName || 'Channel'} Versions)
                </label>
                <span className="text-[10px] text-indigo-600 font-semibold">
                  Showing {currentAvailableTexts.length} variant(s)
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {(currentAvailableTexts.length > 0 ? currentAvailableTexts : [{ name: 't1', versionId: 't1' }]).map((t: any) => {
                  const ed = t.versionId || t.name || 't1';
                  const isEdSelected = selectedEdition.toLowerCase() === ed.toLowerCase();
                  return (
                    <button
                      key={ed}
                      type="button"
                      onClick={() => handleSelectEdition(ed)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase transition-all cursor-pointer ${
                        isEdSelected 
                          ? 'bg-indigo-600 text-white shadow-xs' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                      }`}
                    >
                      {ed}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Subject Line & Preview Text */}
          <div className="space-y-3 pt-1">
            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Subject Line</label>
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Subject line..."
                className="w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Preview Text / Preheader</label>
              <input
                type="text"
                value={preview}
                onChange={e => setPreview(e.target.value)}
                placeholder="Preview text..."
                className="w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Initial Status */}
          <div>
            <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Initial Status</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="pending"
                  checked={status === 'pending'}
                  onChange={() => setStatus('pending')}
                  className="text-indigo-600"
                />
                <span className="text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded font-bold">Pending Draft</span>
              </label>
              <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="approved"
                  checked={status === 'approved'}
                  onChange={() => setStatus('approved')}
                  className="text-emerald-600"
                />
                <span className="text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded font-bold">Approved (Ready)</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="pt-3 border-t flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !selectedCandidate}
              className="px-5 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-all shadow-sm flex items-center gap-1.5 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-3.5 h-3.5" />
                  Add to Plan
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


